// src/pages/PublicBooking.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check, Plus, Minus, Globe, Users, 
  Wifi, Tv, Coffee, Bath, Car, Wind, Home, Snowflake, ThermometerSun, Droplets, Archive, Film, Mic, Dices, ChefHat, Utensils, ShowerHead,
  Dumbbell, Waves, BatteryMedium, ChevronLeft, ChevronRight, Image, CalendarDays, BedDouble } from 'lucide-react';
import Modal from '../components/Modal';

import { format, differenceInDays, getDaysInMonth, startOfMonth, addDays } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { Timestamp, query, where, updateDoc, writeBatch } from 'firebase/firestore';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db } from '../services/firebase';
import { X } from 'lucide-react';
import { addDoc, setDoc, deleteDoc } from 'firebase/firestore';
import DateNotificationForm from '../components/DateNotificationForm';


// Import des composants
import BookingCalendar from '../components/BookingCalendar';
import RoomSelection from '../components/RoomSelection';
import OptionsSelection from '../components/OptionsSelection';
import GuestInfoForm from '../components/GuestInfoForm';
import StepIndicator from '../components/StepIndicator';
import PaymentStep from '../components/PaymentStep';
import RoomSelectionStep from '../components/RoomSelectionStep';
import { generateUniqueReservationNumber } from '../utils/reservationNumberGenerator';

// Import des utilitaires
import { 
  getRoomMaxOccupancy, 
  getRoomPriceByOccupancy, 
  getMaxAvailableOccupancy,
  findAvailableRoom,
  isRoomTypeAvailableForDates 
} from '../utils/roomHelpers';

import { updateClientReservationStats } from '../utils/clientHelpers';
import { findOrCreateClient, recalculateClientStats } from '../services/clientManager';  // ✅ AJOUTER
import { addDailyNotification } from '../utils/dailyNotificationHelper';
import * as mongoClients from '../services/mongoValetierClients';
import { logReservationCreated, logCGRAccepted, logCouponApplied, logClubBlockClick } from '../services/activity';

// ✅ Import Google Analytics
import { initGA4 } from '../services/ga4Config';
import {
  trackPageView,
  trackBookingStarted,
  trackStepProgress,
  trackDatesSelected,
  trackRoomSelected,
  trackOptionAdded,
  trackOptionRemoved,
  trackGuestInfoCompleted,
  trackCouponApplied,
  trackCGVAccepted,
  trackCGVViewed,
  trackPaymentInitiated,
  trackPaymentSuccess,
  trackPaymentFailed,
  trackReservationCancelled,
  trackStepBack,
  trackLanguageChange,
  trackNoRoomsAvailable
} from '../services/ga4Tracking';

import { 
  getDayStatus, 
  getApplicableRules, 
  isValidBookingPattern,
  getPossibleDurationsFromDate 
} from '../utils/availabilityHelpers';

import {
  loadInitialData,
  loadReservationsForMonth,
  createReservation,
  createTemporaryBooking,
  deleteTemporaryBooking,
  updateTemporaryBookingsGuestName,
  loadAndCleanTemporaryBookings,
  normalizeToParisDate
} from '../utils/dataLoaders';

const devLog = (...args) => process.env.NODE_ENV === 'development' && console.log(...args);

// ✅ Fonction pour normaliser les noms (lowercase, sans accents)
const normalizeString = (str) => {
  if (!str) return '';
  return str.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

// ✅ Fonction pour mettre à jour les accompagnants fréquents du client
const updateFrequentCompanions = async (clientId, guestsDetails, reservationId) => {
  try {
    if (!clientId || !guestsDetails || guestsDetails.length <= 1) return;
    
    // ✅ Charger le client depuis MongoDB
    const clientData = await mongoClients.getClientById(clientId);
    
    if (!clientData) return;
    
    const existingCompanions = clientData.frequentCompanions || [];
    
    const batch = writeBatch(db);  // Pour l'index companionIndex uniquement
    
    for (let i = 1; i < guestsDetails.length; i++) {
      const guest = guestsDetails[i];
      if (!guest.firstName || !guest.lastName) continue;
      
      const existingIndex = existingCompanions.findIndex(c => 
        c.firstName?.toLowerCase() === guest.firstName?.toLowerCase() && 
        c.lastName?.toLowerCase() === guest.lastName?.toLowerCase()
      );
      
      if (existingIndex >= 0) {
        existingCompanions[existingIndex] = {
          ...existingCompanions[existingIndex],
          lastSeenOn: new Date().toISOString().split('T')[0],
          timesWithClient: (existingCompanions[existingIndex].timesWithClient || 1) + 1,
          reservations: [
            ...(existingCompanions[existingIndex].reservations || []),
            reservationId
          ].filter((v, i, a) => a.indexOf(v) === i)
        };
      } else {
        existingCompanions.push({
          firstName: guest.firstName,
          lastName: guest.lastName,
          clientId: null,
          firstSeenOn: new Date().toISOString().split('T')[0],
          lastSeenOn: new Date().toISOString().split('T')[0],
          timesWithClient: 1,
          reservations: [reservationId]
        });
      }
      
      // ✅ Mettre à jour l'index companionIndex (reste dans Firebase)
      const searchKey = `${normalizeString(guest.firstName)}_${normalizeString(guest.lastName)}`;
      const indexDocId = `${searchKey}_${clientId}`;
      
      batch.set(doc(db, 'companionIndex', indexDocId), {
        searchKey: searchKey,
        firstName: guest.firstName,
        lastName: guest.lastName,
        clientId: clientId,
        clientFirstName: clientData.firstName || '',
        clientLastName: clientData.lastName || clientData.name || '',
        clientNumber: clientData.clientNumber || null,
        updatedAt: Timestamp.now()
      }, { merge: true });
    }
    
    // ✅ Mettre à jour le client dans MongoDB
    await mongoClients.updateClient(clientId, {
      frequentCompanions: existingCompanions
    });
    
    // Commit l'index Firebase
    await batch.commit();
    
    devLog('✅ Accompagnants fréquents + index mis à jour pour client:', clientId);
    
  } catch (err) {
    console.error('Erreur mise à jour accompagnants fréquents:', err);
  }
};

// Traductions
const translations = {
  fr: {
    // Header
    header: 'RÉSERVATION',
    subtitle: 'Réservation en ligne',
    
    // Steps
    step1: 'Dates',
    step2: 'Chambre',
    step3: 'Options',
    step4: 'Informations',
    step5: 'Paiement',
    
    // Calendrier
    selectDates: 'Sélectionnez vos dates de séjour',
    arrivalOn: 'Arrivée le',
    nights: 'nuits',
    night: 'nuit',
    onlyStay: 'Séjour de',
    possibleStays: 'Séjours possibles :',
    noDurationAvailable: 'Aucune durée disponible à partir de cette date',
    validateDates: 'Valider les dates',
    durationNotAvailable: 'Cette durée n\'est pas disponible',
    selectArrivalDate: 'Sélectionnez votre date d\'arrivée',
    selectDepartureDate: 'Sélectionnez votre date de départ',
    selectRoomTypeBanner: 'Sélectionnez un type de chambre',
    roomsSelected: '{n} chambre{s} sélectionnée{s}',

    // Split stay
    splitStayTitle: 'Séjour possible avec changement de chambre',
    splitStayTitlePart2: 'Deuxième partie de votre séjour',
    splitStayIntro: 'Votre séjour du {from} au {to} est possible avec un changement de chambre en cours de séjour :',
    splitStayInstructions: 'Réservez d\'abord le premier séjour ci-dessous, puis revenez effectuer la seconde réservation.',
    splitStayInstructionsPart2: 'Réservez maintenant la deuxième partie de votre séjour.',
    splitStayBookSegment: 'Réserver du {from} au {to} →',
    splitStayReminderTitle: 'N\'oubliez pas la 2e partie de votre séjour',
    splitStayReminderText: 'Revenez sur cette page pour réserver la deuxième partie de votre séjour.',
    splitStayContinuePart2: 'Réserver la 2e partie du séjour →',

    // Chambres
    selectRooms: 'Sélection des chambres',
    noRoomsAvailable: 'Aucune chambre disponible',
    allRoomsBooked: 'Toutes nos chambres sont déjà réservées ou en cours de réservation pour les dates sélectionnées :',
    chooseOtherDates: 'Choisir d\'autres dates',
    refreshPage: 'Actualiser la page',
    roomsAvailable: 'chambre{s} disponible{s}',
    maxPerBooking: '(max {max} par réservation)',
    perNight: 'par nuit',
    numberOfRooms: 'Sélectionnez le nombre de chambres',
    maxRoomsReached: '{max} chambre{s} sélectionnée{s} (maximum par réservation). Pour une chambre supplémentaire, contactez-nous.',
    lastRoomSelected: 'Vous avez sélectionné la dernière chambre disponible pour ces dates.',
    lastRoomsSelected: 'Vous avez sélectionné les {n} dernières chambres disponibles pour ces dates.',
    occupancyPerRoom: 'Occupation par chambre:',
    room: 'Chambre',
    persons: 'personnes',
    person: 'personne',
    pers: 'pers.',
    summary: 'Récapitulatif:',
    roomsFor: 'chambre{s} pour',
    maxSimultaneous: 'Il est possible de réserver {max} chambre{s} maximum simultanément.',
    contactForMore: 'Si vous souhaitez réserver plus de {max} chambre{s}, merci de nous contacter.',
    backToDates: 'Retour aux dates',
    continueToOptions: 'Continuer vers les options',
    
    // Options
    optionsAndServices: 'Options et services supplémentaires',
    personalizeYourStay: 'Personnalisez votre séjour avec nos services optionnels',
    noOptionsAvailable: 'Aucune option disponible pour le moment.',
    contactForSpecialRequests: 'Contactez-nous pour des demandes spéciales.',
    perPersonPerDay: 'par personne et par jour',
    perPerson: 'par personne',
    perDay: 'par jour',
    perRoom: 'par chambre',
    forBooking: 'pour la réservation',
    total: 'Total',
    optionsTotal: 'Total des options:',
    backToRooms: 'Retour aux chambres',
    continue: 'Continuer',
    
    // Informations client
    yourInformation: 'Vos informations',
    lastName: 'Nom',
    firstName: 'Prénom',
    company: 'Société (optionnel)',
    email: 'Email',
    phone: 'Téléphone',
    address: 'Adresse',
    city: 'Ville',
    postalCode: 'Code postal',
    country: 'Pays',
    message: 'Message (optionnel)',
    additionalInfo: 'Informations complémentaires...',
    bookingSummary: 'Récapitulatif de la réservation',
    roomType: 'Type de chambre:',
    numberOfGuests: 'Nombre de personnes:',
    dates: 'Dates:',
    numberOfNights: 'Nombre de nuits:',
    accommodation: 'Hébergement',
    selectedOptions: 'Options sélectionnées:',
    requiredFields: '* Les champs marqués d\'une étoile sont obligatoires',
    note: 'Note:',
    youAreBooking: 'Vous réservez {rooms} chambre{s} pour {guests} personne{s} au total',
    back: 'Retour',
    continueToPayment: 'Continuer vers le paiement',
    
    // Paiement
    paymentSecurity: 'Sécurisation de la réservation',
    
    // Messages d'erreur
    allFieldsRequired: 'Veuillez remplir tous les champs obligatoires',
    errorOccurred: 'Une erreur est survenue. Veuillez réessayer.',
    roomsNoLongerAvailable: 'Désolé, une ou plusieurs chambres ont été réservées entre-temps. Veuillez recommencer.',
    notEnoughRooms: 'Désolé, seulement {available} chambre(s) disponible(s) sur les {requested} demandée(s).',
    
    // Modal de confirmation
    bookingConfirmed: 'Demande envoyée !',
    bookedRooms: '{count} chambre{s} à confirmer',
    number: 'Numéro de demande :',
    
    // Messages calendrier
    allRoomsOccupied: 'Toutes les chambres sont occupées',
    arrivalImpossible: 'Arrivée impossible ce jour',
    departureImpossible: 'Départ impossible ce jour',
    onlyStayDuration: 'Séjour de {durations} uniquement',
    
    // Jours de la semaine
    monday: 'Lun',
    tuesday: 'Mar',
    wednesday: 'Mer',
    thursday: 'Jeu',
    friday: 'Ven',
    saturday: 'Sam',
    sunday: 'Dim',
    
    // Légende calendrier
    available: 'Disponible',
    selected: 'Sélectionné',
    fullOrClosed: 'Complet / Fermé',
    departureOnly: 'Départ possible · Complet à l\'arrivée',
    oneNightAvailable: 'Séjour d\'une nuit possible',
    dateSelectionHint: 'Cliquez sur la date d\'arrivée, puis sur la date de départ et validez.',

    // CGR
    cgvAccept: 'J\'ai lu et j\'accepte les',
    cgvLink: 'Conditions Générales de Réservation',
    cgvRequired: 'Vous devez accepter les Conditions Générales de Réservation pour continuer'
  },
  en: {
    // Header
    header: 'BOOKING',
    subtitle: 'Online booking',
    
    // Steps
    step1: 'Dates',
    step2: 'Room',
    step3: 'Options',
    step4: 'Information',
    step5: 'Payment',
    
    // Calendar
    selectDates: 'Select your stay dates',
    arrivalOn: 'Arrival on',
    nights: 'nights',
    night: 'night',
    onlyStay: 'Stay of',
    possibleStays: 'Possible stays:',
    noDurationAvailable: 'No duration available from this date',
    validateDates: 'Confirm dates',
    durationNotAvailable: 'This duration is not available',
    selectArrivalDate: 'Select your arrival date',
    selectDepartureDate: 'Select your departure date',
    selectRoomTypeBanner: 'Select a room type',
    roomsSelected: '{n} room{s} selected',

    // Split stay
    splitStayTitle: 'Your stay is possible with a room change',
    splitStayTitlePart2: 'Second part of your stay',
    splitStayIntro: 'Your stay from {from} to {to} is possible with a room change mid-stay:',
    splitStayInstructions: 'Please book the first stay below, then come back to complete the second booking.',
    splitStayInstructionsPart2: 'Now book the second part of your stay.',
    splitStayBookSegment: 'Book from {from} to {to} →',
    splitStayReminderTitle: 'Don\'t forget the 2nd part of your stay',
    splitStayReminderText: 'Come back to this page to book the second part of your stay.',
    splitStayContinuePart2: 'Book the 2nd part of your stay →',

    // Rooms
    selectRooms: 'Room selection',
    noRoomsAvailable: 'No rooms available',
    allRoomsBooked: 'All our rooms are already booked or being booked for the selected dates:',
    chooseOtherDates: 'Choose other dates',
    refreshPage: 'Refresh page',
    roomsAvailable: 'room{s} available',
    maxPerBooking: '(max {max} per booking)',
    perNight: 'per night',
    numberOfRooms: 'Select number of rooms',
    maxRoomsReached: '{max} room{s} selected (maximum per booking). For an additional room, contact us.',
    lastRoomSelected: 'You have selected the last available room for these dates.',
    lastRoomsSelected: 'You have selected the last {n} available rooms for these dates.',
    occupancyPerRoom: 'Occupancy per room:',
    room: 'Room',
    persons: 'persons',
    person: 'person',
    pers: 'pers.',
    summary: 'Summary:',
    roomsFor: 'room{s} for',
    maxSimultaneous: 'It is possible to book {max} room{s} maximum simultaneously.',
    contactForMore: 'If you wish to book more than {max} room{s}, please contact us.',
    backToDates: 'Back to dates',
    continueToOptions: 'Continue to options',
    
    // Options
    optionsAndServices: 'Additional options and services',
    personalizeYourStay: 'Personalize your stay with our optional services',
    noOptionsAvailable: 'No options available at the moment.',
    contactForSpecialRequests: 'Contact us for special requests.',
    perPersonPerDay: 'per person per day',
    perPerson: 'per person',
    perDay: 'per day',
    perRoom: 'per room',
    forBooking: 'for the booking',
    total: 'Total',
    optionsTotal: 'Options total:',
    backToRooms: 'Back to rooms',
    continue: 'Continue',
    
    // Guest information
    yourInformation: 'Your information',
    lastName: 'Last name',
    firstName: 'First name',
    company: 'Company (optional)',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    city: 'City',
    postalCode: 'Postal code',
    country: 'Country',
    message: 'Message (optional)',
    additionalInfo: 'Additional information...',
    bookingSummary: 'Booking summary',
    roomType: 'Room type:',
    numberOfGuests: 'Number of guests:',
    dates: 'Dates:',
    numberOfNights: 'Number of nights:',
    accommodation: 'Accommodation',
    selectedOptions: 'Selected options:',
    requiredFields: '* Fields marked with a star are required',
    note: 'Note:',
    youAreBooking: 'You are booking {rooms} room{s} for {guests} person{s} in total',
    back: 'Back',
    continueToPayment: 'Continue to payment',
    
    // Payment
    paymentSecurity: 'Securing the reservation',
    
    // Error messages
    allFieldsRequired: 'Please fill in all required fields',
    errorOccurred: 'An error occurred. Please try again.',
    roomsNoLongerAvailable: 'Sorry, one or more rooms have been booked in the meantime. Please start again.',
    notEnoughRooms: 'Sorry, only {available} room(s) available out of {requested} requested.',
    
    // Confirmation modal
    bookingConfirmed: 'Request sent!',
    bookedRooms: '{count} room{s} to confirm',
    number: 'Request number:',
    
    // Calendar messages
    allRoomsOccupied: 'All rooms are fully booked',
    arrivalImpossible: 'Arrival not possible this day',
    departureImpossible: 'Departure not possible this day',
    onlyStayDuration: 'Stay of {durations} only',
    
    // Days of the week
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun',
    
    // Calendar legend
    available: 'Available',
    selected: 'Selected',
    fullOrClosed: 'Full / Closed',
    departureOnly: 'Departure possible · Full on arrival',
    oneNightAvailable: 'One-night stay possible',
    dateSelectionHint: 'Click your arrival date, then your departure date, and confirm.',
    
    // CGR
    cgvAccept: 'I have read and accept the',
    cgvLink: 'Booking Terms and Conditions',
    cgvRequired: 'You must accept the Booking Terms and Conditions to continue'
  }
};


// Map des amenities avec icônes et traductions
const amenityConfig = {
  wifi: { icon: Wifi, fr: 'Wifi', en: 'Wifi' },
  tv: { icon: Tv, fr: 'TV', en: 'TV' },
  airConditioner: { icon: Snowflake, fr: 'Climatisation', en: 'Air Conditioning' },
  fan: { icon: Wind, fr: 'Ventilateur', en: 'Fan' },
  heater: { icon: ThermometerSun, fr: 'Chauffage', en: 'Heating' },
  refrigerator: { icon: Home, fr: 'Réfrigérateur', en: 'Refrigerator' },
  microwave: { icon: Home, fr: 'Micro-ondes', en: 'Microwave' },
  coffee: { icon: Coffee, fr: 'Cafetière', en: 'Coffee Machine' },
  shower: { icon: ShowerHead, fr: 'Douche', en: 'Shower' },
  bathtub: { icon: Bath, fr: 'Baignoire', en: 'Bathtub' },
  parking: { icon: Car, fr: 'Parking', en: 'Parking' },
  breakfast: { icon: Utensils, fr: 'Petit-déjeuner', en: 'Breakfast' },
  restaurant: { icon: ChefHat, fr: 'Restaurant', en: 'Restaurant' },
  gym: { icon: Dumbbell, fr: 'Gym', en: 'Gym' },
  indoorPool: { icon: Waves, fr: 'Piscine intérieure', en: 'Indoor Pool' },
  outdoorPool: { icon: Waves, fr: 'Piscine extérieure', en: 'Outdoor Pool' },
  sauna: { icon: ThermometerSun, fr: 'Sauna', en: 'Sauna' },
  hammam: { icon: Droplets, fr: 'Hammam', en: 'Hammam' },
  nordicBath: { icon: Archive, fr: 'Bain Nordique', en: 'Nordic Bath' },
  cinema: { icon: Film, fr: 'Cinéma', en: 'Cinema' },
  karaoke: { icon: Mic, fr: 'Karaoké', en: 'Karaoke' },
  boardGames: { icon: Dices, fr: 'Jeux de Société', en: 'Board Games' },
  electricCharging: { icon: BatteryMedium, fr: 'Recharge Véhicule', en: 'EV Charging' },

};



// Nombre de personnes d'un sejour, a partir des chambres deja construites.
//
// Pour un sejour AVEC CHANGEMENT DE CHAMBRE, les chambres se SUCCEDENT dans le temps :
// c'est la meme personne qui demenage, on ne l'additionne pas avec elle-meme. On prend
// le PIC d'occupation simultanee. Pour un sejour ordinaire (aucun segment), toutes les
// chambres sont occupees en meme temps : le pic est la somme, comme avant.
//
// Ce qui etait ecrit avant : nombre de chambres x selectedOccupancy, ou selectedOccupancy
// n'est PAS le nombre de personnes saisi par le client mais la valeur posee au clic sur la
// carte du type de chambre (roomType.defaultOccupancy, absent partout, donc 2). Resultat :
// « 2 personnes » sur TOUS les sejours splittes, quelle que soit la reponse du client.
// Une cliente seule s'est vue compter 2 personnes, et sa taxe de sejour doubler.
// Le vrai chiffre etait pourtant deja la, dans roomsArray[].occupancy.
const nombreDePersonnes = (roomsArray) => {
  const enDate = (v) => (v?.toDate ? v.toDate() : (v ? new Date(v) : null));
  const debutsDeSegment = roomsArray
    .map((r) => enDate(r.segmentCheckIn))
    .filter(Boolean)
    .map((d) => d.getTime());

  // Aucun segment : sejour ordinaire, toutes les chambres sont simultanees.
  if (debutsDeSegment.length === 0) {
    return roomsArray.reduce((somme, r) => somme + (r.occupancy || 0), 0);
  }

  // Le pic d'un ensemble d'intervalles se produit forcement au debut de l'un d'eux.
  let pic = 0;
  debutsDeSegment.forEach((instant) => {
    let simultanees = 0;
    roomsArray.forEach((r) => {
      const ci = enDate(r.segmentCheckIn);
      const co = enDate(r.segmentCheckOut);
      // Une chambre sans segment couvre tout le sejour : elle compte toujours.
      if (!ci || !co) { simultanees += (r.occupancy || 0); return; }
      if (ci.getTime() <= instant && instant < co.getTime()) simultanees += (r.occupancy || 0);
    });
    if (simultanees > pic) pic = simultanees;
  });

  // Filet : JAMAIS la somme (ce serait le defaut qu'on corrige). Au pire, la plus
  // grande chambre seule.
  return pic || Math.max(0, ...roomsArray.map((r) => r.occupancy || 0));
};

// ✅ DST-safe : date-fns differenceInDays compte des jours locaux pleins et se trompe
// de -1 nuit quand le séjour traverse le changement d'heure (dernier dim. d'octobre),
// car selectedCheckIn/selectedCheckOut sont ancrés en UTC (T12:00:00Z) donc à une heure
// locale différente selon heure d'été/hiver. Un calcul en millisecondes est insensible au DST.
const nightsBetween = (checkOutDate, checkInDate) =>
  Math.round((checkOutDate.getTime() - checkInDate.getTime()) / 86400000);

function PublicBooking({ adminMode = false, userData = null }) {
  const navigate = useNavigate();
  
  // État pour la langue
const [language, setLanguage] = useState(() => {
    // Vérifier si un paramètre lang est dans l'URL
    const urlParams = new URLSearchParams(window.location.search);
    const urlLang = urlParams.get('lang');
    
    if (urlLang === 'en' || urlLang === 'fr') {
      localStorage.setItem('bookingLanguage', urlLang);
      return urlLang;
    }
    
    return localStorage.getItem('bookingLanguage') || (navigator.language?.startsWith('fr') ? 'fr' : 'en');
  });
  
  const t = translations[language];
  const dateLocale = language === 'fr' ? fr : enUS;

  // Déclare au navigateur la vraie langue affichée (évite que la traduction
  // automatique de Chrome se déclenche à tort — cause d'écran blanc au paiement).
  useEffect(() => { document.documentElement.lang = language; }, [language]);

  const [selectedImageIndex, setSelectedImageIndex] = useState({});
  
  // Textes personnalisables pour le booking light
  const [bookingLightTextFr, setBookingLightTextFr] = useState('');
  const [bookingLightTextEn, setBookingLightTextEn] = useState('');
  
const [dateNotificationEnabled, setDateNotificationEnabled] = useState(false);
const [dateNotificationTitleFr, setDateNotificationTitleFr] = useState('');
const [dateNotificationTitleEn, setDateNotificationTitleEn] = useState('');
const [dateNotificationTextFr, setDateNotificationTextFr] = useState('');
const [dateNotificationTextEn, setDateNotificationTextEn] = useState('');

  // Module WhatsApp Last Minute
  const [bookingWhatsappContentFr, setBookingWhatsappContentFr] = useState('');
  const [bookingWhatsappContentEn, setBookingWhatsappContentEn] = useState('');
  const [bookingWhatsappQrCodeUrl, setBookingWhatsappQrCodeUrl] = useState('');
  const [bookingWhatsappLink, setBookingWhatsappLink] = useState('');

  // Fonction pour changer la langue
const toggleLanguage = () => {
  const newLang = language === 'fr' ? 'en' : 'fr';
  setLanguage(newLang);
  localStorage.setItem('bookingLanguage', newLang);
  trackLanguageChange(newLang);
};
  
  // Fonction helper pour pluriel
  const pluralize = (text, count) => {
    return text.replace(/{s}/g, count > 1 ? 's' : '');
  };
  
  // États principaux
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);
  const [selectedCheckOut, setSelectedCheckOut] = useState(null);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [invalidDateMessage, setInvalidDateMessage] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [selectedOccupancy, setSelectedOccupancy] = useState({});
  const [selectedRoomCount, setSelectedRoomCount] = useState({});
  const [selectedRoomOccupancies, setSelectedRoomOccupancies] = useState({});
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [loadedOptions, setLoadedOptions] = useState([]);
  const [step, setStep] = useState(1);
  const [splitStayInfo, setSplitStayInfo] = useState(null);
  const [splitBlockedMessage, setSplitBlockedMessage] = useState(null);
  const [splitStayImpossibleMessage, setSplitStayImpossibleMessage] = useState({ fr: '', en: '' });
  const [splitSegmentSelections, setSplitSegmentSelections] = useState([]);
  // Scroll to top quand on change de step
useEffect(() => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, [step]);
  const [loading, setLoading] = useState(true);
  const [loadingReservations, setLoadingReservations] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(null);

const [cachedMonthPrices, setCachedMonthPrices] = useState({});
const [cacheTimestamp, setCacheTimestamp] = useState(null);
const [loadingMonthPrices, setLoadingMonthPrices] = useState(false);

const [displayPrices, setDisplayPrices] = useState({});
  const [longStayDiscounts, setLongStayDiscounts] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  // États pour les temporary bookings
  const [sessionId] = useState(() => crypto.randomUUID());
  const [temporaryBookingIds, setTemporaryBookingIds] = useState([]);
  const [temporaryBookings, setTemporaryBookings] = useState([]);
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  const [pendingRoomSelections, setPendingRoomSelections] = useState({});

  // États des données
  const [roomTypes, setRoomTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [availabilityRules, setAvailabilityRules] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [roomBlocks, setRoomBlocks] = useState([]);
  const [availableOptions, setAvailableOptions] = useState([]);
  
  const [defaultRules, setDefaultRules] = useState({
    minNights: 1,
    maxNights: 30,
    maxRoomsPerBooking: adminMode ? 999 : 5,
    customMessage: '',
    weekConfig: {
      monday: 'flexible',
      tuesday: 'flexible',
      wednesday: 'flexible',
      thursday: 'flexible',
      friday: 'flexible',
      saturday: 'flexible',
      sunday: 'flexible'
    }
  });
  
  // État pour l'horizon de réservation
const [horizonSettings, setHorizonSettings] = useState({
  maxPublicBookableDate: '',
  maxMemberBookableDate: '', // AJOUT
  maxBookingHorizonMonths: 12,
  enabled: true
});
  
  // État des informations client
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    firstName: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: language === 'fr' ? 'France' : 'United States',
    message: ''
  });

// États pour le paiement et les tentatives
const [paymentIntent, setPaymentIntent] = useState(null);
const [paymentAttempts, setPaymentAttempts] = useState(0);
const [createdReservationId, setCreatedReservationId] = useState(null);
const [showPaymentFailedModal, setShowPaymentFailedModal] = useState(false);


// Callback onBlur des 4 champs identité → écriture after_form si tous remplis (ré-écrit à chaque correction)
  const handleIdentityFieldBlur = () => {
    const { name, firstName, email, phone } = guestInfo;
    if (name && firstName && email && phone && bookingStartedRef.current && selectedCheckIn && selectedCheckOut && selectedRoomType) {
      hasFilledFormRef.current = true;
      savedGuestInfoRef.current = { name, firstName, email, phone };
      if (temporaryBookingIds.length > 0) {
        updateTemporaryBookingsGuestName(temporaryBookingIds, `${firstName} ${name}`);
      }
      setDoc(doc(db, 'lost_reservations', sessionId), {
        trackingId: bookingTrackingId,
        sessionId: sessionId,
        clientIP: userIP || null,
        roomTypeId: selectedRoomType,
        checkIn: Timestamp.fromDate(selectedCheckIn),
        checkOut: Timestamp.fromDate(selectedCheckOut),
        abandonStage: 'after_form',
        language: language || null,
        guestInfo: { name, firstName, email, phone },
        createdAt: Timestamp.now(),
        abandonedAt: Timestamp.now()
      }).catch(e => console.error('Erreur tracking after_form:', e));
    }
  };

    const [customAnswers, setCustomAnswers] = useState({});
  const [selectedClientId, setSelectedClientId] = useState(null);  // ✅ AJOUTER CETTE LIGNE
  const [guestsDetails, setGuestsDetails] = useState([]);  // ✅ RENOMMÉ : Détails des accompagnants

  // États pour le coupon de réservation
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [availableCoupons, setAvailableCoupons] = useState([]);
  const [showCouponSuccessModal, setShowCouponSuccessModal] = useState(false);

  const [loadingModal, setLoadingModal] = useState(false);

  // 🔵 Retour de redirection 3D Secure (Payment Element).
  // Quand la banque exige une redirection plein écran pour le 3DS, Stripe renvoie le client
  // sur cette page avec ?setup_intent=...&redirect_status=succeeded. L'état React du tunnel
  // est alors perdu : on finalise la réservation côté serveur via /confirm-reservation
  // (qui lit payment_safety écrit avant le 3DS), puis on affiche l'écran de confirmation.
  const [threeDSError, setThreeDSError] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnedSetupIntent = params.get('setup_intent');
    const redirectStatus = params.get('redirect_status');
    if (!returnedSetupIntent) return;

    // Nettoyer l'URL immédiatement (évite un re-déclenchement + ne laisse pas traîner le client_secret)
    window.history.replaceState({}, '', `${window.location.origin}${window.location.pathname}`);

    if (redirectStatus && redirectStatus !== 'succeeded') {
      setThreeDSError(true);
      return;
    }

    (async () => {
      setLoadingModal(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://valetier.maisonceronne.com/api';
        const resp = await fetch(`${apiUrl}/valetier/confirm-reservation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ setupIntentId: returnedSetupIntent })
        });
        const result = await resp.json();

        if (result.success) {
          let roomCount = 1;
          let reservationNumber = result.reservationNumber || '';
          if (result.reservationId) {
            try {
              const rSnap = await getDoc(doc(db, 'reservations', result.reservationId));
              if (rSnap.exists()) {
                const rd = rSnap.data();
                roomCount = rd.rooms?.length || 1;
                if (!reservationNumber) reservationNumber = rd.number || '';
              }
            } catch (_) {}
          }
          try { trackPaymentSuccess(result.reservationId, 0, language); } catch (_) {}
          setConfirmationModal({ roomCount, reservationNumber });
        } else {
          console.error('❌ confirm-reservation (retour 3DS) échec:', result.error);
          setThreeDSError(true);
        }
      } catch (e) {
        console.error('❌ Erreur retour 3DS:', e);
        setThreeDSError(true);
      } finally {
        setLoadingModal(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // États pour les CGV
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [cgvLinks, setCgvLinks] = useState({ fr: '', en: '' });
  const [cgvTexts, setCgvTexts] = useState({ fr: '', en: '' });
  const [cgvUseText, setCgvUseText] = useState(false);
  const [cgvModalOpen, setCgvModalOpen] = useState(false);
  const [cgvRequired, setCgvRequired] = useState(true);
  const [cgvError, setCgvError] = useState(false);
  const [userIP, setUserIP] = useState(null);
  
  // Message personnalisé calendrier
  const [calendarMessage, setCalendarMessage] = useState({ fr: '', en: '' });

  // Encart « Espace Club » sous le calendrier — piloté depuis Paramètres > Général
  // de VALETIER (même document Firestore settings/general).
  const [clubBlock, setClubBlock] = useState({
    enabled: false, logoUrl: '', textFr: '', textEn: '', buttonFr: '', buttonEn: '', url: '', textBold: false
  });

  // Charger les paramètres CGV
  useEffect(() => {
    const loadCGVSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setCgvLinks({
            fr: data.cgvLinkFr || '',
            en: data.cgvLinkEn || ''
          });
          setCgvTexts({
            fr: data.cgvTextFr || '',
            en: data.cgvTextEn || ''
          });
          setCgvUseText(data.cgvUseText || false);
          setCgvRequired(data.cgvRequired !== false);
          setCalendarMessage({
            fr: data.calendarMessageFr || '',
            en: data.calendarMessageEn || ''
          });
          setSplitStayImpossibleMessage({
            fr: data.splitStayImpossibleMessageFr || '',
            en: data.splitStayImpossibleMessageEn || ''
          });
          setClubBlock({
            enabled: data.clubBlockEnabled === true,
            logoUrl: data.clubBlockLogoUrl || '',
            textFr: data.clubBlockTextFr || '',
            textEn: data.clubBlockTextEn || '',
            buttonFr: data.clubBlockButtonFr || '',
            buttonEn: data.clubBlockButtonEn || '',
            url: data.clubBlockUrl || '',
            textBold: data.clubBlockTextBold === true
          });
        }
      } catch (error) {
        console.error('Erreur chargement paramètres CGV:', error);
      }
    };
    loadCGVSettings();
  }, []);

  // Récupérer l'IP de l'utilisateur (uniquement en mode public)
  useEffect(() => {
    const fetchIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setUserIP(data.ip);
      } catch (error) {
        devLog('Impossible de récupérer IP:', error);
      }
    };
    if (!adminMode) {
      fetchIP();
    }
  }, [adminMode]);

// États pour le tracking des abandons
  const [bookingTrackingId] = useState(() => crypto.randomUUID());
  const hasFilledFormRef = useRef(false);
  const bookingStartedRef = useRef(false);
  const paymentAttemptedRef = useRef(false);
const guestInfoRef = useRef(guestInfo);
const savedGuestInfoRef = useRef({});
const stepRef = useRef(step);

// Mettre à jour la référence quand guestInfo change
useEffect(() => {
  guestInfoRef.current = guestInfo;
}, [guestInfo]);

useEffect(() => {
  stepRef.current = step;
}, [step]);



// Nettoyage des temporary bookings à la fermeture + tracking des abandons
  useEffect(() => {
    const cleanup = async () => {
      devLog('🧹 Nettoyage des temporary bookings...');
      for (const id of temporaryBookingIds) {
        await deleteTemporaryBooking(id);
      }
      
      // Tracker l'abandon si réservation non finalisée
if (hasFilledFormRef.current && bookingStartedRef.current && selectedCheckIn && selectedCheckOut && selectedRoomType) {
      try {
const abandonStage = paymentAttemptedRef.current ? 'payment_abandoned' : 'after_form';
          // ID déterministe = 1 seul document par session (écrase le précédent)
          const lostDocId = `${sessionId}`;
          
          await setDoc(doc(db, 'lost_reservations', lostDocId), {
            trackingId: bookingTrackingId,
            sessionId: sessionId,
            clientIP: userIP || null,
            roomTypeId: selectedRoomType,
            checkIn: Timestamp.fromDate(selectedCheckIn),
            checkOut: Timestamp.fromDate(selectedCheckOut),
            abandonStage: abandonStage,

guestInfo: {
  name: savedGuestInfoRef.current.name || '',
  firstName: savedGuestInfoRef.current.firstName || '',
  email: savedGuestInfoRef.current.email || '',
  phone: savedGuestInfoRef.current.phone || ''
},


            createdAt: Timestamp.now(),
            abandonedAt: Timestamp.now()
          });
          
          devLog('📊 Abandon tracké:', abandonStage);
        } catch (error) {
          console.error('Erreur tracking abandon:', error);
        }
      }
    };

    window.addEventListener('beforeunload', cleanup);
    
    return () => {
      cleanup();
      window.removeEventListener('beforeunload', cleanup);
    };
}, [temporaryBookingIds, bookingTrackingId, sessionId, selectedCheckIn, selectedCheckOut, selectedRoomType, userIP]);

// Écriture proactive à l'entrée du step 5 — fallback after_form si step 4 ne l'a pas déjà écrit
useEffect(() => {
  if (step === 5 && hasFilledFormRef.current && selectedCheckIn && selectedCheckOut && selectedRoomType) {
    const writeAfterFormFallback = async () => {
      try {
        await setDoc(doc(db, 'lost_reservations', sessionId), {
          trackingId: bookingTrackingId,
          sessionId: sessionId,
          clientIP: userIP || null,
          roomTypeId: selectedRoomType,
          checkIn: Timestamp.fromDate(selectedCheckIn),
          checkOut: Timestamp.fromDate(selectedCheckOut),
          abandonStage: 'after_form',
          language: language || null,
          guestInfo: {
            name: savedGuestInfoRef.current.name || '',
            firstName: savedGuestInfoRef.current.firstName || '',
            email: savedGuestInfoRef.current.email || '',
            phone: savedGuestInfoRef.current.phone || ''
          },
          createdAt: Timestamp.now(),
          abandonedAt: Timestamp.now()
        });
      } catch (error) {
        console.error('Erreur tracking after_form (step 5 fallback):', error);
      }
    };
    writeAfterFormFallback();
  }
}, [step, sessionId, bookingTrackingId, selectedCheckIn, selectedCheckOut, selectedRoomType, userIP]);


  // Chargement initial des données
useEffect(() => {
  // ✅ Initialiser GA4
  initGA4();
  trackPageView();
  
  loadData();
}, []);


// Charger les coupons disponibles en mode admin
  useEffect(() => {
    devLog('🔍 useEffect coupons - adminMode:', adminMode);
    if (adminMode) {
      const loadCoupons = async () => {
        try {
          devLog('📥 Chargement des coupons...');
          const couponsSnapshot = await getDocs(collection(db, 'coupons'));
          devLog('📦 Coupons récupérés:', couponsSnapshot.docs.length);
          const coupons = couponsSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(c => !c.isUsed && !c.isDisabled)
            .sort((a, b) => (a.code || '').localeCompare(b.code || ''));
          devLog('✅ Coupons disponibles:', coupons);
          setAvailableCoupons(coupons);
        } catch (error) {
          console.error('❌ Erreur chargement coupons:', error);
        }
      };
      loadCoupons();
    }
  }, [adminMode]);

  // Chargement des réservations quand le mois change
  useEffect(() => {
    if (roomTypes.length > 0) {
      loadMonthReservations();
    }
  }, [currentMonth]);

  // Recharger les temporary bookings toutes les 5 secondes à l'étape 1
  useEffect(() => {
    if (step === 1) {
      const interval = setInterval(async () => {
        const tempBookings = await loadAndCleanTemporaryBookings();
        setTemporaryBookings(tempBookings);
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [step]);

  // Chargement des données fraîches à l'étape 2
  
useEffect(() => {
    if (step === 2) {
      const loadFreshData = async () => {
        devLog('🔄 Chargement des données fraîches pour l\'étape 2...');
        
        const tempBookings = await loadAndCleanTemporaryBookings();
        setTemporaryBookings(tempBookings);
        
        const monthReservations = await loadReservationsForMonth(currentMonth);
        setReservations(monthReservations);
        
        // Charger les prix calculés pour l'affichage
        setLoadingPrices(true);
        const prices = {};
        for (const roomType of roomTypes) {
          const nights = nightsBetween(selectedCheckOut, selectedCheckIn);
          let totalPrice = 0;
          const currentDate = new Date(selectedCheckIn);
          
          for (let i = 0; i < nights; i++) {
            const dailyPrice = await getComputedPrice(roomType.id, currentDate);
            totalPrice += dailyPrice;
            currentDate.setDate(currentDate.getDate() + 1);
          }
          
          prices[roomType.id] = Math.round(totalPrice / nights);
        }
        setDisplayPrices(prices);
        setLoadingPrices(false);
      };
      loadFreshData();
    }
  }, [step, currentMonth, selectedCheckIn, selectedCheckOut, roomTypes]);

useEffect(() => {
  // Nettoyer le cache après 12h d'inactivité
  return () => {
    setCachedMonthPrices({});
    setCacheTimestamp(null);
  };
}, []);



// Fonction de chargement des données
const loadData = async () => {
  try {
    setLoading(true);
    
    // 🚀 OPTIMISATION : Charger TOUTES les données en parallèle avec Promise.all()
    const [
      data,
      roomBlocksSnapshot,
      monthReservations,
      tempBookings,
      settingsDoc
    ] = await Promise.all([
      loadInitialData(adminMode),
      getDocs(collection(db, 'room_blocks')),
      loadReservationsForMonth(currentMonth),
      loadAndCleanTemporaryBookings(),
      getDoc(doc(db, 'settings', 'general'))
    ]);
    
    // Le filtrage est déjà fait dans loadInitialData selon adminMode
    setRoomTypes(data.roomTypes);
    setRooms(data.rooms);





    setAvailabilityRules(data.availabilityRules);
    setBlockedDates(data.blockedDates);
    setDefaultRules(data.defaultRules);
    devLog('RULES LOADED:', data.defaultRules);
    
    // Charger les paramètres d'horizon
    try {
      const horizonDoc = await getDocs(query(collection(db, 'settings'), where('type', '==', 'booking_horizon')));
      if (!horizonDoc.empty) {
        const horizonData = horizonDoc.docs[0].data();
setHorizonSettings({
  maxPublicBookableDate: horizonData.maxPublicBookableDate || '',
  maxMemberBookableDate: horizonData.maxMemberBookableDate || '', // AJOUT
  maxBookingHorizonMonths: horizonData.maxBookingHorizonMonths || 12,
  enabled: horizonData.enabled !== false
});
        devLog('HORIZON SETTINGS LOADED:', horizonData);
      }
    } catch (error) {
      console.error('Erreur chargement horizon settings:', error);
    }
    
    // Traiter les room_blocks
    const roomBlocksData = roomBlocksSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: normalizeToParisDate(doc.data().startDate),
      endDate: normalizeToParisDate(doc.data().endDate)
    }));
    setRoomBlocks(roomBlocksData);
    devLog('ROOM BLOCKS LOADED:', roomBlocksData.length);
    
    // Traiter les réservations
    setReservations(monthReservations);
    
    // Traiter les temporary bookings
    setTemporaryBookings(tempBookings);
    
    // Charger la config du booking light (textes personnalisables)
    try {
      if (settingsDoc.exists()) {
        const settingsData = settingsDoc.data();
        setBookingLightTextFr(settingsData.bookingLightTextFr || '');
        setBookingLightTextEn(settingsData.bookingLightTextEn || '');
        setDateNotificationEnabled(settingsData.bookingDateNotificationEnabled || false);
setDateNotificationTitleFr(settingsData.bookingDateNotificationTitleFr || '');
setDateNotificationTitleEn(settingsData.bookingDateNotificationTitleEn || '');
setDateNotificationTextFr(settingsData.bookingDateNotificationTextFr || '');
setDateNotificationTextEn(settingsData.bookingDateNotificationTextEn || '');
        setBookingWhatsappContentFr(settingsData.bookingWhatsappContentFr || '');
        setBookingWhatsappContentEn(settingsData.bookingWhatsappContentEn || '');
        setBookingWhatsappQrCodeUrl(settingsData.bookingWhatsappQrCodeUrl || '');
        setBookingWhatsappLink(settingsData.bookingWhatsappLink || '');
      }
    } catch (error) {
      console.error('Erreur chargement config booking light:', error);
    }
    
  } catch (error) {
    console.error('Erreur chargement données:', error);
  } finally {
    setLoading(false);
  }
};

  // Fonction de chargement des réservations du mois
  const loadMonthReservations = async () => {
    try {
      setLoadingReservations(true);
      const monthReservations = await loadReservationsForMonth(currentMonth);
      setReservations(monthReservations);
    } catch (error) {
      console.error('Erreur chargement réservations:', error);
    } finally {
      setLoadingReservations(false);
    }
  };

  // Calculer le nombre de chambres disponibles pour un type à une date
const getAvailableRoomCount = useCallback((roomTypeId, date, restrictToRoomIds = null) => {
  const roomType = roomTypes.find(rt => rt.id === roomTypeId);
  let roomsOfType = roomType?.assignedRooms ?
    rooms.filter(r => roomType.assignedRooms.includes(r.id)) : [];
  if (restrictToRoomIds && restrictToRoomIds.length > 0) {
    roomsOfType = roomsOfType.filter(r => restrictToRoomIds.includes(r.id));
  }
  const totalRooms = roomsOfType.length;
  
  if (totalRooms === 0) return 0;
  
  let occupiedCount = 0;
  
// Vérifier les room_blocks (blocages manuels depuis le calendrier admin)
const blockedRoomIds = new Set();

roomBlocks.forEach(block => {
  const blockStart = new Date(block.startDate);
  const blockEnd = new Date(block.endDate);
  blockStart.setHours(0,0,0,0);
  blockEnd.setHours(0,0,0,0);
  const checkDate = new Date(date);
  checkDate.setHours(0,0,0,0);
  
  // Un blocage se termine à midi du dernier jour, donc checkDate < blockEnd
  if (checkDate >= blockStart && checkDate < blockEnd) {
    const isThisType = roomsOfType.some(r => r.id === block.roomId);
    if (isThisType) {
      blockedRoomIds.add(block.roomId); // Set évite les doublons
    }
  }
});

occupiedCount += blockedRoomIds.size;
  
  // Vérifier les réservations existantes
  reservations.forEach(res => {
    if (res.status === 'cancelled') return;

// Dates enveloppe pré-normalisées (fallback si la chambre n'a pas de segment propre)
const envCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
const envCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);

// NORMALISER la date testée à minuit pour éviter les problèmes de timezone
const normalizedDate = new Date(date);
normalizedDate.setHours(0, 0, 0, 0);

// La nuit `date` tombe-t-elle dans [ci, co) ? (comparaison à minuit)
const nightIsInPeriod = (ci, co) => {
  const ciM = new Date(ci); ciM.setHours(0, 0, 0, 0);
  const coM = new Date(co); coM.setHours(0, 0, 0, 0);
  return normalizedDate >= ciM && normalizedDate < coM;
};

    if (res.rooms && Array.isArray(res.rooms)) {
      res.rooms.forEach(room => {
        const isThisType = roomsOfType.some(r => r.id === room.roomId);
        if (!isThisType) return;
        // ✅ Séjour avec changement de chambre : chaque chambre n'est occupée que sur SON segment
        const roomCI = room.segmentCheckIn ? normalizeToParisDate(room.segmentCheckIn) : envCheckIn;
        const roomCO = room.segmentCheckOut ? normalizeToParisDate(room.segmentCheckOut) : envCheckOut;
        if (nightIsInPeriod(roomCI, roomCO)) {
          occupiedCount++;
        }
      });
    } else if (res.roomId) {
      const isThisType = roomsOfType.some(r => r.id === res.roomId);
      if (isThisType && nightIsInPeriod(envCheckIn, envCheckOut)) {
        occupiedCount++;
      }
    }
  });


  // Vérifier les temporary bookings
  temporaryBookings.forEach(temp => {
    if (temp.sessionId === sessionId) return;
    
    const isThisType = roomsOfType.some(r => r.id === temp.roomId);
    if (isThisType) {
      const isInPeriod = date >= temp.checkIn && date < temp.checkOut;
      if (isInPeriod) {
        occupiedCount++;
      }
    }
  });
  
const available = totalRooms - occupiedCount;

// Dans getAvailableRoomCount, remplace le debug ligne 1027 par :
if (date.getDate() === 23 && date.getMonth() === 0 && roomTypeId === 'aBKs2ZK19lBTvbGjMJ2N') {
  const occupiedRoomIdsDebug = [];
  reservations.forEach(res => {
    if (res.status === 'cancelled') return;
    const checkIn = res.checkIn?.toDate ? res.checkIn.toDate() : new Date(res.checkIn);
    const checkOut = res.checkOut?.toDate ? res.checkOut.toDate() : new Date(res.checkOut);
    checkIn.setHours(0,0,0,0);
    checkOut.setHours(0,0,0,0);
    const d = new Date(date);
    d.setHours(0,0,0,0);
    if (d >= checkIn && d < checkOut) {
      if (res.rooms && Array.isArray(res.rooms)) {
        res.rooms.forEach(room => {
          const isThisType = roomsOfType.some(r => r.id === room.roomId);
          if (isThisType) {
            occupiedRoomIdsDebug.push({resId: res.id, roomId: room.roomId});
          }
        });
      }
    }
  });
  
  devLog('🔴 CHAMBRES OCCUPÉES 23 JAN - JS La Voisine:', occupiedRoomIdsDebug);
}

return available;
}, [rooms, reservations, temporaryBookings, roomTypes, sessionId, roomBlocks]);

  // Calculer le nombre max de chambres disponibles pour une période
  const getMaxAvailableRoomsForPeriod = useCallback((roomTypeId, checkIn, checkOut, ignoreBookingLimit = false) => {
    if (!checkIn || !checkOut) return 0;

    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    let roomsOfType = roomType?.assignedRooms ?
      rooms.filter(r => roomType.assignedRooms.includes(r.id)) : [];
    let ruleRoomIds = null;

    // Si la règle de plus haute priorité pour ces dates/nuits a des roomIds, restreindre aux chambres éligibles
    if (availabilityRules && checkIn && checkOut) {
      const checkInD = new Date(checkIn); checkInD.setHours(0,0,0,0);
      const checkOutD = new Date(checkOut); checkOutD.setHours(0,0,0,0);
      const nights = Math.round((checkOutD - checkInD) / 86400000);
      const candidates = availabilityRules.filter(rule => {
        if (rule.active === false) return false;
        if (rule.roomTypes && rule.roomTypes.length > 0 && !rule.roomTypes.includes(roomTypeId)) return false;
        if (nights < rule.minNights || nights > rule.maxNights) return false;
        const rs = new Date(rule.startDate); rs.setHours(0,0,0,0);
        const re = new Date(rule.endDate); re.setHours(0,0,0,0);
        return checkInD >= rs && checkInD <= re;
      });
      if (candidates.length > 0) {
        const highestPriority = Math.max(...candidates.map(r => r.priority || 1));
        const topRule = candidates.find(r => (r.priority || 1) === highestPriority);
        if (topRule && topRule.roomIds && topRule.roomIds.length > 0) {
          roomsOfType = roomsOfType.filter(r => topRule.roomIds.includes(r.id));
          ruleRoomIds = topRule.roomIds;
        }
      }
    }

    let alreadySelectedCount = 0;
    Object.entries(pendingRoomSelections).forEach(([typeId, selectedInfo]) => {
if (typeId !== roomTypeId && selectedInfo.assignedRooms) {
  alreadySelectedCount += selectedInfo.assignedRooms.filter(roomId =>
          roomsOfType.some(r => r.id === roomId)
        ).length;
      }
    });
    
    let minAvailable = roomsOfType.length - alreadySelectedCount;
    
    const currentDate = new Date(checkIn);
    while (currentDate < checkOut) {
      const availableCount = getAvailableRoomCount(roomTypeId, currentDate, ruleRoomIds) - alreadySelectedCount;
      minAvailable = Math.min(minAvailable, availableCount);
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    let totalOtherRooms = 0;
    Object.entries(selectedRoomCount || {}).forEach(([typeId, count]) => {
      if (typeId !== roomTypeId) {
        totalOtherRooms += (count || 0);
      }
    });
    
const maxRoomsAllowed = (adminMode || ignoreBookingLimit) ? 999 : (defaultRules.maxRoomsPerBooking || 5);
    const remainingAllowance = Math.max(0, maxRoomsAllowed - totalOtherRooms);
    minAvailable = Math.min(minAvailable, remainingAllowance);
    
    return Math.max(0, minAvailable);
}, [rooms, roomTypes, getAvailableRoomCount, pendingRoomSelections, defaultRules, selectedRoomCount, availabilityRules]);

  // Wrappers avec les bonnes signatures
  const getDayStatusWrapper = useCallback((date, roomTypeId = null) => {
    return getDayStatus(date, roomTypeId, blockedDates, rooms, availabilityRules, defaultRules, false, horizonSettings, adminMode);
  }, [availabilityRules, blockedDates, rooms, defaultRules, horizonSettings, adminMode]);

  const getApplicableRulesWrapper = useCallback((checkIn, checkOut, roomTypeId = null) => {
    return getApplicableRules(checkIn, checkOut, roomTypeId, availabilityRules);
  }, [availabilityRules]);

  const isValidBookingPatternWrapper = useCallback((checkIn, checkOut, roomTypeId = null) => {
    return isValidBookingPattern(checkIn, checkOut, roomTypeId, blockedDates, availabilityRules, getApplicableRulesWrapper, defaultRules);
  }, [blockedDates, availabilityRules, getApplicableRulesWrapper, defaultRules]);

  const getPossibleDurationsFromDateWrapper = useCallback((checkInDate) => {
    return getPossibleDurationsFromDate(checkInDate, selectedRoomType, availabilityRules, defaultRules, false, rooms, reservations);
  }, [selectedRoomType, availabilityRules, defaultRules, rooms, reservations]);

  const getMaxAvailableOccupancyWrapper = useCallback((roomTypeId, checkIn, checkOut) => {
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    if (!roomType) return 2;
    
    const typeMaxOccupancy = roomType.maxOccupancy || roomType.maxGuests || 2;
    
    const roomsOfType = roomType?.assignedRooms ? 
      rooms.filter(r => roomType.assignedRooms.includes(r.id)) : [];
    
    let hasAvailableRoom = false;
    
    for (const room of roomsOfType) {
      const hasConflict = reservations.some(res => {
        if (res.status === 'cancelled') return false;
        
        let isRoomInReservation = false;
        if (res.rooms && Array.isArray(res.rooms)) {
          isRoomInReservation = res.rooms.some(r => r.roomId === room.id);
        } else if (res.roomId === room.id) {
          isRoomInReservation = true;
        }
        
        if (!isRoomInReservation) return false;

        if (res.isSplitStay && res.splitSegments) {
          const seg = res.splitSegments.find(s => (s.roomIds || []).includes(room.id));
          if (seg) {
            const segCI = normalizeToParisDate(seg.checkIn instanceof Object ? seg.checkIn.toDate() : new Date(seg.checkIn));
            const segCO = normalizeToParisDate(seg.checkOut instanceof Object ? seg.checkOut.toDate() : new Date(seg.checkOut));
            return (checkIn < segCO && checkOut > segCI);
          }
        }
        const resCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
        const resCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);
        return (checkIn < resCheckOut && checkOut > resCheckIn);
      });

      if (!hasConflict) {
        hasAvailableRoom = true;
        break;
      }
    }
    
    return hasAvailableRoom ? typeMaxOccupancy : 0;
  }, [rooms, roomTypes, reservations]);

  const isRoomTypeAvailableForDatesWrapper = useCallback((roomTypeId, checkIn, checkOut) => {
    return isRoomTypeAvailableForDates(roomTypeId, checkIn, checkOut, rooms, roomTypes, reservations, isValidBookingPatternWrapper);
  }, [rooms, roomTypes, reservations, isValidBookingPatternWrapper]);

  // Cache pour les statuts des jours du mois
  const monthDayStatuses = useMemo(() => {
    const cache = {};
    const daysInMonth = getDaysInMonth(currentMonth);
    
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
      const key = format(date, 'yyyy-MM-dd');
      cache[key] = getDayStatusWrapper(date);
    }
    
    return cache;
  }, [currentMonth, getDayStatusWrapper]);

  // Cache pour les prix minimums ET disponibilités du mois

const monthMinPrices = useMemo(() => {
  const cache = {};
  const daysInMonth = getDaysInMonth(currentMonth);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i);
    const key = format(date, 'yyyy-MM-dd');

    // Ignorer les dates passées
    if (date < today) {
      cache[key] = null; // null = gris (pas de prix)
      continue;
    }

    const generalStatus = getDayStatusWrapper(date);
    const isOutOnly = generalStatus && generalStatus.canDepart && !generalStatus.canArrive;

    let minPrice = null;
    let hasValidAvailability = false;
    let isAnyRoomTypeOpen = false;

    for (const roomType of roomTypes) {
      const status = getDayStatusWrapper(date, roomType.id);

      if (!status || !status.isOpen || status.isBlocked) {
        continue;
      }

      isAnyRoomTypeOpen = true;

      const availableRooms = getAvailableRoomCount(roomType.id, date);

      if (availableRooms > 0) {
        hasValidAvailability = true;

        // Utiliser le prix de base directement (pas de chargement async)
        const price = roomType.basePrice;

        if (!isOutOnly && (minPrice === null || price < minPrice)) {
          minPrice = price;
        }
      }
    }

if (isOutOnly) {
  // Pour un jour OUT : vérifier si la veille (dernier jour du séjour) a de la dispo
  const previousDay = new Date(date);
  previousDay.setDate(previousDay.getDate() - 1);

  let hasPreviousDayAvailability = false;
  for (const roomType of roomTypes) {
    const prevStatus = getDayStatusWrapper(previousDay, roomType.id);
    if (!prevStatus || !prevStatus.isOpen || prevStatus.isBlocked) continue;
    const availablePrevDay = getAvailableRoomCount(roomType.id, previousDay);
    if (availablePrevDay > 0) {
      hasPreviousDayAvailability = true;
      break;
    }
  }

  // Vert si la veille a de la dispo, rouge sinon
  cache[key] = hasPreviousDayAvailability ? -1 : null;
} else if (!hasValidAvailability && isAnyRoomTypeOpen && generalStatus && generalStatus.canDepart) {
  // Départ possible selon les règles, mais chambres pleines ce soir-là
  // Vérifier si la veille a de la dispo → un séjour finissant ici reste bookable
  const previousDay = new Date(date);
  previousDay.setDate(previousDay.getDate() - 1);

  let hasPreviousDayAvailability = false;
  for (const roomType of roomTypes) {
    const prevStatus = getDayStatusWrapper(previousDay, roomType.id);
    if (!prevStatus || !prevStatus.isOpen || prevStatus.isBlocked) continue;
    const availablePrevDay = getAvailableRoomCount(roomType.id, previousDay);
    if (availablePrevDay > 0) {
      hasPreviousDayAvailability = true;
      break;
    }
  }

  // -2 = case diagonale (départ possible, arrivée pleine)
  cache[key] = hasPreviousDayAvailability ? -2 : null;
} else {
  // Jours normaux : vert si dispo, rouge sinon
  cache[key] = hasValidAvailability ? minPrice : null;
}
  }
  
  return cache;
  
}, [currentMonth, roomTypes, getDayStatusWrapper, roomBlocks, reservations, temporaryBookings]);

// Fonctions pour naviguer dans les images
  const handlePreviousImage = (roomTypeId, e) => {
    e.stopPropagation();
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const currentIndex = selectedImageIndex[roomTypeId] || 0;
    const photos = roomType.photos || [];
    
    if (photos.length > 0) {
      const newIndex = currentIndex === 0 ? photos.length - 1 : currentIndex - 1;
      setSelectedImageIndex(prev => ({ ...prev, [roomTypeId]: newIndex }));
    }
  };

  const handleNextImage = (roomTypeId, e) => {
    e.stopPropagation();
    const roomType = roomTypes.find(rt => rt.id === roomTypeId);
    const currentIndex = selectedImageIndex[roomTypeId] || 0;
    const photos = roomType.photos || [];
    
    if (photos.length > 0) {
      const newIndex = (currentIndex + 1) % photos.length;
      setSelectedImageIndex(prev => ({ ...prev, [roomTypeId]: newIndex }));
    }
  };

  // Gestion du clic sur une date
  const handleDateClick = (date) => {
    // ✅ NORMALISATION : Transformer en chaîne YYYY-MM-DD puis recréer à minuit pile
    const dateString = format(date, 'yyyy-MM-dd');
    const normalizedDate = new Date(dateString + 'T12:00:00Z');
    
    const status = getDayStatusWrapper(normalizedDate, selectedRoomType);
    
  // 🔍 DEBUG
  devLog('Clic sur', normalizedDate.toLocaleDateString(), {
    selectedRoomType,
    status,
    availabilityByType: roomTypes.map(rt => ({
      type: rt.nameFr,
      available: getAvailableRoomCount(rt.id, normalizedDate)
    }))
  });

    if (!status.isOpen || status.isBlocked) {
      return;
    }
    
    // En mode sélection départ, vérifier toutes les nuits du séjour (pas seulement la nuit du départ)
    const isSelectingCheckOut = !!(selectedCheckIn && !selectedCheckOut && normalizedDate > selectedCheckIn);
    const hasAnyAvailability = isSelectingCheckOut
      ? (() => {
          const nights = nightsBetween(normalizedDate, selectedCheckIn);
          for (let n = 0; n < nights; n++) {
            const nightDate = new Date(selectedCheckIn.getTime() + n * 86400000);
            if (!roomTypes.some(rt => getAvailableRoomCount(rt.id, nightDate) > 0)) return false;
          }
          return true;
        })()
      : roomTypes.some(rt => getAvailableRoomCount(rt.id, normalizedDate) > 0);
    if (!hasAnyAvailability) {
      setInvalidDateMessage({
        date: normalizedDate,
        message: t.allRoomsOccupied
      });
      setTimeout(() => setInvalidDateMessage(null), 3000);
      return;
    }

if (!selectedCheckIn && !status.canArrive && !adminMode) {
      if (status.customMessage) {
        setInvalidDateMessage({
          date: normalizedDate,
          message: status.customMessage
        });
        setTimeout(() => setInvalidDateMessage(null), 5000);
      } else {
        setInvalidDateMessage({
          date: normalizedDate,
          message: t.arrivalImpossible
        });
        setTimeout(() => setInvalidDateMessage(null), 3000);
      }
      return;
    }
    
if (selectedCheckIn && !selectedCheckOut && normalizedDate > selectedCheckIn) {
      const nights = nightsBetween(normalizedDate, selectedCheckIn);
      const possible = adminMode ? [nights] : getPossibleDurationsFromDateWrapper(selectedCheckIn);
      
      if (!status.canDepart || (!adminMode && !possible.includes(nights))) {
        if (status.canDepart && !possible.includes(nights)) {
          const minDur = Math.min(...possible);
          const maxDur = Math.max(...possible);
          const durationsText = minDur === maxDur
            ? `${minDur} ${minDur > 1 ? t.nights : t.night}`
            : `${minDur} ${language === 'fr' ? 'à' : 'to'} ${maxDur} ${maxDur > 1 ? t.nights : t.night}`;
          const message = possible.length > 0 
            ? t.onlyStayDuration.replace('{durations}', durationsText)
            : t.noDurationAvailable;
          
          setInvalidDateMessage({
            date: normalizedDate,
            message: message
          });
          
          setTimeout(() => setInvalidDateMessage(null), 3000);
        }
        return;
      }
    }
    
    if (selectedCheckIn && format(selectedCheckIn, 'yyyy-MM-dd') === format(normalizedDate, 'yyyy-MM-dd')) {
      setSelectedCheckIn(null);
      setSelectedCheckOut(null);
      setHoveredDate(null);
      setInvalidDateMessage(null);
      return;
    }
    
    if (selectedCheckOut && format(selectedCheckOut, 'yyyy-MM-dd') === format(normalizedDate, 'yyyy-MM-dd')) {
      setSelectedCheckOut(null);
      setHoveredDate(null);
      setInvalidDateMessage(null);
      return;
    }
    
    if (!selectedCheckIn || (selectedCheckIn && selectedCheckOut)) {
      if (!status.canArrive && !adminMode) {
        return;
      }
      setSelectedCheckIn(normalizedDate);
      setSelectedCheckOut(null);
      setHoveredDate(null);
      setInvalidDateMessage(null);
      setSelectedOccupancy({});
      setSelectedRoomCount({});
      setSelectedRoomOccupancies({});
      setSelectedOptions([]);
    } else if (normalizedDate > selectedCheckIn) {
      if (!status.canDepart && !adminMode) {
        setInvalidDateMessage({
          date: normalizedDate,
          message: t.departureImpossible
        });
        setTimeout(() => setInvalidDateMessage(null), 3000);
        return;
      }
      
      const nights = nightsBetween(normalizedDate, selectedCheckIn);
      const possibleDurations = getPossibleDurationsFromDateWrapper(selectedCheckIn);
      
      if (possibleDurations.includes(nights)) {
        setSelectedCheckOut(normalizedDate);
        setHoveredDate(null);
        setInvalidDateMessage(null);
      } else {
        const minDur = Math.min(...possibleDurations);
        const maxDur = Math.max(...possibleDurations);
        const durationsText = minDur === maxDur
          ? `${minDur} ${minDur > 1 ? t.nights : t.night}`
          : `${minDur} ${language === 'fr' ? 'à' : 'to'} ${maxDur} ${maxDur > 1 ? t.nights : t.night}`;
        const message = possibleDurations.length > 0 
          ? t.onlyStayDuration.replace('{durations}', durationsText)
          : t.noDurationAvailable;
        
        setInvalidDateMessage({
          date: normalizedDate,
          message: message
        });
        
        setTimeout(() => setInvalidDateMessage(null), 3000);
      }
    } else {
      if (!status.canArrive && !adminMode) {
        return;
      }
      setSelectedCheckIn(normalizedDate);
      setSelectedCheckOut(null);
      setHoveredDate(null);
      setInvalidDateMessage(null);
      setSelectedOccupancy({});
      setSelectedRoomCount({});
      setSelectedRoomOccupancies({});
      setSelectedOptions([]);
    }
  };

  // Détecte si un séjour segmenté est possible quand aucune chambre n'est dispo sur la période complète
  // Chaque nuit de la plage a-t-elle au moins une chambre libre (tous types confondus) ?
  // Sert à distinguer « vraiment complet » (au moins une nuit à 0) de « faisable mais
  // avec plusieurs changements de chambre » (toutes les nuits couvrables) → impossible en ligne.
  const isEveryNightCoverable = useCallback((checkIn, checkOut) => {
    const cur = new Date(checkIn);
    const end = new Date(checkOut);
    while (cur < end) {
      const anyRoom = roomTypes.some(rt => getAvailableRoomCount(rt.id, cur) > 0);
      if (!anyRoom) return false;
      cur.setDate(cur.getDate() + 1);
    }
    return true;
  }, [roomTypes, getAvailableRoomCount]);

  const detectSplitStay = useCallback((checkIn, checkOut) => {
    const nights = nightsBetween(checkOut, checkIn);
    if (nights < 2) return null;

    // Partir du plus grand segment possible (nuits-1) → trouver la coupure la plus tardive
    // On check uniquement la dispo physique, pas les règles de pattern (qui s'appliquent à la validation finale)
    for (let midOffset = nights - 1; midOffset >= 1; midOffset--) {
      const midDate = addDays(checkIn, midOffset);

      const firstTypes = roomTypes.filter(rt => {
        return getMaxAvailableRoomsForPeriod(rt.id, checkIn, midDate) > 0;
      });
      if (!firstTypes.length) continue;

      const secondTypes = roomTypes.filter(rt => {
        return getMaxAvailableRoomsForPeriod(rt.id, midDate, checkOut) > 0;
      });
      if (!secondTypes.length) continue;

      return {
        segments: [
          { checkIn, checkOut: midDate, roomTypes: firstTypes },
          { checkIn: midDate, checkOut, roomTypes: secondTypes }
        ],
        originalCheckIn: checkIn,
        originalCheckOut: checkOut,
        currentSegment: 0,
        showingIntro: true
      };
    }
    return null;
  }, [roomTypes, getMaxAvailableRoomsForPeriod]);

  // Calcule les prix et initialise les compteurs de chambres pour une période donnée
  const proceedToRoomSelection = useCallback(async (checkIn, checkOut, totalNightsOverride, isSplitSegment = false) => {
    setLoadingPrices(true);
    const prices = {};
    const nights = nightsBetween(checkOut, checkIn);
    const nightsForDiscount = totalNightsOverride ?? nights;

    for (const roomType of roomTypes) {
      let totalPrice = 0;
      const currentDate = new Date(checkIn);
      for (let i = 0; i < nights; i++) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const cacheId = `${roomType.id}_${dateStr}`;
        try {
          const priceDoc = await getDoc(doc(db, 'computed_prices', cacheId));
          totalPrice += priceDoc.exists() ? priceDoc.data().finalPrice : (roomType.basePrice || 0);
        } catch {
          totalPrice += roomType.basePrice || 0;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
      prices[roomType.id] = Math.round(totalPrice / nights);
    }
    setDisplayPrices(prices);
    setLoadingPrices(false);

    // Réductions longue durée — une seule requête pour tous les types
    const discounts = {};
    try {
      const lsSnap = await getDocs(collection(db, 'long_stay_prices'));
      lsSnap.forEach(d => {
        const data = d.data();
        const start = data.startDate?.toDate();
        const end = data.endDate?.toDate();
        if (data.active !== false && start && end && checkIn >= start && checkIn <= end && nightsForDiscount >= data.minNights && nightsForDiscount <= data.maxNights) {
          Object.entries(data.variations || {}).forEach(([rtId, value]) => {
            discounts[rtId] = { name: data.name, reduction: Math.abs(value), type: data.reductionType };
          });
        }
      });
    } catch (e) { console.error('Erreur long_stay_prices:', e); }
    setLongStayDiscounts(discounts);

    const initialOccupancy = {};
    const initialRoomCount = {};
    roomTypes.forEach(rt => {
      const maxRooms = getMaxAvailableRoomsForPeriod(rt.id, checkIn, checkOut);
      if (maxRooms > 0 && (isSplitSegment || isRoomTypeAvailableForDatesWrapper(rt.id, checkIn, checkOut))) {
        initialOccupancy[rt.id] = rt.defaultOccupancy || 2;
        initialRoomCount[rt.id] = 0;
      }
    });
    setSelectedOccupancy(initialOccupancy);
    setSelectedRoomCount(initialRoomCount);
    setSelectedRoomOccupancies({});
  }, [roomTypes, getMaxAvailableRoomsForPeriod, isRoomTypeAvailableForDatesWrapper]);

  const handleStepClick = (targetStep) => {
    if (targetStep >= step) return;
    if (targetStep <= 1) {
      setSplitStayInfo(null);
      setSelectedRoomType(null);
      setSelectedRoomCount({});
      setSelectedOccupancy({});
      setSelectedRoomOccupancies({});
      setPendingRoomSelections({});
      setSelectedOptions([]);
      setAppliedCoupon(null);
      setStep(1);
    } else if (targetStep === 2) {
      setSplitStayInfo(null);
      setSelectedOptions([]);
      setAppliedCoupon(null);
      setStep(2);
    } else if (targetStep === 3) {
      setAppliedCoupon(null);
      setStep(3);
    } else {
      setStep(targetStep);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Validation des dates et passage à l'étape 2
const handleValidateDates = async () => {
  if (selectedCheckIn && selectedCheckOut) {
    setTransitioning(true);
    bookingStartedRef.current = true;
    trackDatesSelected(selectedCheckIn, selectedCheckOut, language);

    // Tracking before_form — fire-and-forget, ne bloque pas la transition
    setDoc(doc(db, 'lost_reservations', sessionId), {
      trackingId: bookingTrackingId,
      sessionId: sessionId,
      clientIP: userIP || null,
      roomTypeId: selectedRoomType || null,
      roomCount: selectedRoomCount || null,
      occupancy: selectedOccupancy || null,
      language: language || null,
      checkIn: Timestamp.fromDate(selectedCheckIn),
      checkOut: Timestamp.fromDate(selectedCheckOut),
      abandonStage: 'before_form',
      guestInfo: { name: '', firstName: '', email: '', phone: '' },
      createdAt: Timestamp.now(),
      abandonedAt: Timestamp.now()
    }).catch(e => console.error('Erreur tracking before_form:', e));

    await proceedToRoomSelection(selectedCheckIn, selectedCheckOut);

    const hasAvailableRooms = roomTypes.some(rt =>
      getMaxAvailableRoomsForPeriod(rt.id, selectedCheckIn, selectedCheckOut) > 0 &&
      isRoomTypeAvailableForDatesWrapper(rt.id, selectedCheckIn, selectedCheckOut)
    );

    if (!hasAvailableRooms && !adminMode) {
      const split = detectSplitStay(selectedCheckIn, selectedCheckOut);
      if (split) {
        setSplitStayInfo(split);
        trackStepProgress(2, language);
        setTransitioning(false);
        setStep(2);
        return;
      }
      // Ni séjour complet, ni split simple (1 changement). Si chaque nuit est couvrable
      // séparément, c'est qu'il faudrait changer de chambre plusieurs fois → impossible en ligne.
      if (isEveryNightCoverable(selectedCheckIn, selectedCheckOut)) {
        setSplitBlockedMessage({
          fr: splitStayImpossibleMessage.fr || "Ces dates nécessitent un changement de chambre sur l'ensemble des nuits. Il n'est pas possible de réaliser la réservation en ligne, mais vous pouvez nous appeler pour que nous puissions organiser votre séjour.",
          en: splitStayImpossibleMessage.en || "These dates require a room change for every night of the stay. It is not possible to complete the booking online, but you can call us so that we can arrange your stay."
        });
        setTransitioning(false);
        return;
      }
    }

    setSplitStayInfo(null);
    trackStepProgress(2, language);
    setTransitioning(false);
    setStep(2);
  }
};

  // Passage de l'étape 2 à 3 avec création des temporary bookings
const handleContinueToOptions = async () => {
  setTransitioning(true);
  try {
    devLog('🔎 Recherche de chambres disponibles pour tous les types sélectionnés...');
    
    const freshTempBookings = await loadAndCleanTemporaryBookings();
    const freshReservations = await loadReservationsForMonth(currentMonth);
    
    // 🔵 NOUVEAU : Récupérer TOUS les types sélectionnés
    const selectedTypes = Object.entries(selectedRoomCount).filter(([typeId, count]) => count > 0);
    
    if (selectedTypes.length === 0) {
      setTransitioning(false);
      alert(t.errorOccurred);
      return;
    }
    
    devLog(`📊 Types sélectionnés:`, selectedTypes.map(([id, count]) => {
      const rt = roomTypes.find(r => r.id === id);
      return `${rt?.nameFr || rt?.name} (${count})`;
    }).join(', '));
    
    const allSelectedRoomIds = [];
    const newTempIds = [];
    
    // 🔵 BOUCLER sur chaque type sélectionné
    for (const [roomTypeId, roomCount] of selectedTypes) {
      const roomType = roomTypes.find(rt => rt.id === roomTypeId);
      if (!roomType) continue;
      
      let roomsOfType = roomType?.assignedRooms ?
        rooms.filter(r => roomType.assignedRooms.includes(r.id)) : [];

      // Filtrer par roomIds si la règle applicable en spécifie
      const _ciD = new Date(selectedCheckIn); _ciD.setHours(0,0,0,0);
      const _coD = new Date(selectedCheckOut); _coD.setHours(0,0,0,0);
      const _nights = Math.round((_coD - _ciD) / 86400000);
      const _ruleCandidates = (availabilityRules || []).filter(rule => {
        if (rule.active === false) return false;
        if (rule.roomTypes?.length > 0 && !rule.roomTypes.includes(roomTypeId)) return false;
        if (_nights < (rule.minNights || 1) || _nights > (rule.maxNights || 30)) return false;
        const rs = new Date(rule.startDate); rs.setHours(0,0,0,0);
        const re = new Date(rule.endDate); re.setHours(0,0,0,0);
        return _ciD >= rs && _ciD <= re;
      });
      if (_ruleCandidates.length > 0) {
        const _topPriority = Math.max(..._ruleCandidates.map(r => r.priority || 1));
        const _topRule = _ruleCandidates.find(r => (r.priority || 1) === _topPriority);
        if (_topRule?.roomIds?.length > 0) {
          const _filtered = roomsOfType.filter(r => _topRule.roomIds.includes(r.id));
          if (_filtered.length > 0) roomsOfType = _filtered;
        }
      }

      const requiredOccupancy = selectedOccupancy[roomTypeId] || roomType.defaultOccupancy || 2;

      devLog(`\n🔍 Traitement: ${roomType.nameFr || roomType.name}`);
      devLog(`   Recherche ${roomCount} chambre(s) pour ${requiredOccupancy} personnes`);
      
      const availableRooms = [];
      
      // 🔵 Vérifier chaque chambre du type
      for (const room of roomsOfType) {
        const roomCapacity = room.maxOccupancy || roomType?.maxOccupancy || roomType?.maxGuests || 2;
        
        if (roomCapacity < requiredOccupancy) {
          devLog(`   ❌ Chambre ${room.roomNumber} ignorée (capacité ${roomCapacity} < ${requiredOccupancy})`);
          continue;
        }
        
        // Vérifier les réservations
        const hasReservation = freshReservations.some(res => {
          if (res.status === 'cancelled') return false;
          
          let isRoomInReservation = false;
          if (res.rooms && Array.isArray(res.rooms)) {
            isRoomInReservation = res.rooms.some(r => r.roomId === room.id);
          } else if (res.roomId === room.id) {
            isRoomInReservation = true;
          }
          
          if (!isRoomInReservation) return false;

          if (res.isSplitStay && res.splitSegments) {
            const seg = res.splitSegments.find(s => (s.roomIds || []).includes(room.id));
            if (seg) {
              const segCI = normalizeToParisDate(seg.checkIn instanceof Object ? seg.checkIn.toDate() : new Date(seg.checkIn));
              const segCO = normalizeToParisDate(seg.checkOut instanceof Object ? seg.checkOut.toDate() : new Date(seg.checkOut));
              return (selectedCheckIn < segCO && selectedCheckOut > segCI);
            }
          }
          const resCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
          const resCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);
          return (selectedCheckIn < resCheckOut && selectedCheckOut > resCheckIn);
        });

        const hasTemp = freshTempBookings.some(temp =>
          temp.roomId === room.id &&
          temp.sessionId !== sessionId &&
          selectedCheckIn < temp.checkOut && 
          selectedCheckOut > temp.checkIn
        );

        const hasBlock = roomBlocks.some(block => {
          if (block.roomId !== room.id) return false;
          
          const blockStart = new Date(block.startDate);
          const blockEnd = new Date(block.endDate);
          blockStart.setHours(0,0,0,0);
          blockEnd.setHours(0,0,0,0);
          
          return (selectedCheckIn < blockEnd && selectedCheckOut > blockStart);
        });

        if (!hasReservation && !hasTemp && !hasBlock) {
          // 🔵 NOUVEAU : Stocker avec le nombre de typeIds pour priorisation
          availableRooms.push({
            room: room,
            typeIdsCount: room.typeIds?.length || 1,
            roomCapacity: roomCapacity
          });
        }
      }
      
      // 🔵 NOUVEAU : TRIER par spécialisation (moins de typeIds = priorité)
      availableRooms.sort((a, b) => a.typeIdsCount - b.typeIdsCount);
      
      devLog(`   ✅ ${availableRooms.length} chambres disponibles (triées par spécialisation)`);
      availableRooms.forEach(item => {
        devLog(`      - ${item.room.roomNumber}: ${item.typeIdsCount} type(s), capacité ${item.roomCapacity}`);
      });
      
      if (availableRooms.length < roomCount) {
        alert(t.notEnoughRooms
          .replace('{available}', availableRooms.length)
          .replace('{requested}', roomCount) + 
          ` (${roomType.nameFr || roomType.name})`
        );
        
        // Nettoyer les temporary bookings déjà créés
        for (const id of newTempIds) {
          await deleteTemporaryBooking(id);
        }
        setTransitioning(false);
        return;
      }
      
      // 🔵 Prendre les N premières chambres (déjà triées par spécialisation)
      const selectedRooms = availableRooms.slice(0, roomCount);
      
      // 🔵 Créer les temporary bookings
      for (const item of selectedRooms) {
        const tempId = await createTemporaryBooking(
          item.room.id,
          selectedCheckIn,
          selectedCheckOut,
          sessionId
        );
        newTempIds.push(tempId);
        allSelectedRoomIds.push(item.room.id);
        devLog(`   ✅ Chambre ${item.room.roomNumber} bloquée (${item.typeIdsCount} type(s))`);
      }
    }
    
    devLog(`\n🎯 Total: ${allSelectedRoomIds.length} chambres bloquées`);
    
    setTemporaryBookingIds(newTempIds);
    setSelectedRoomIds(allSelectedRoomIds);
    
    // Définir le premier type comme "selectedRoomType" pour l'UI
if (selectedTypes.length > 0) {
  setSelectedRoomType(selectedTypes[0][0]);
}

trackRoomSelected(selectedTypes[0][0], allSelectedRoomIds.length, getTotalGuests(), language);
trackStepProgress(3, language);

    // Mise à jour before_form avec chambre/pers maintenant connus
    try {
      await setDoc(doc(db, 'lost_reservations', sessionId), {
        trackingId: bookingTrackingId,
        sessionId: sessionId,
        clientIP: userIP || null,
        roomTypeId: selectedTypes[0][0] || null,
        roomTypeIds: selectedTypes.map(t => t[0]).filter(Boolean),
        totalRooms: getTotalRooms(),
        totalGuests: getTotalGuests(),
        language: language || null,
        checkIn: Timestamp.fromDate(selectedCheckIn),
        checkOut: Timestamp.fromDate(selectedCheckOut),
        abandonStage: 'before_form',
        guestInfo: { name: '', firstName: '', email: '', phone: '' },
        createdAt: Timestamp.now(),
        abandonedAt: Timestamp.now()
      });
    } catch (e) { console.error('Erreur mise à jour before_form:', e); }

    // --- SPLIT STAY : logique de segment ---
    if (splitStayInfo && !splitStayInfo.showingIntro) {
      let segRoomTotal = 0;
      const segNights = nightsBetween(selectedCheckOut, selectedCheckIn);
      Object.entries(selectedRoomCount).forEach(([typeId, count]) => {
        if (count > 0) {
          const rt = roomTypes.find(t => t.id === typeId);
          if (rt) {
            const pricePerNight = displayPrices[rt.id] || rt.basePrice || 0;
            segRoomTotal += applyLongStayToTotal(rt.id, pricePerNight, segNights, count);
          }
        }
      });
      const segData = {
        checkIn: selectedCheckIn,
        checkOut: selectedCheckOut,
        selectedRoomType: selectedTypes[0]?.[0] || null,
        selectedRoomCount: { ...selectedRoomCount },
        selectedOccupancy: { ...selectedOccupancy },
        selectedRoomOccupancies: { ...selectedRoomOccupancies },
        selectedRoomIds: [...allSelectedRoomIds],
        displayPrices: { ...displayPrices },
        longStayDiscounts: { ...longStayDiscounts },
        temporaryBookingIds: [...newTempIds],
        segmentRoomTotal: segRoomTotal,
      };

      if (splitStayInfo.currentSegment < splitStayInfo.segments.length - 1) {
        setSplitSegmentSelections(prev => [...prev, segData]);
        setTemporaryBookingIds(newTempIds);
        setSelectedRoomType(null);
        setSelectedRoomCount({});
        setSelectedOccupancy({});
        setSelectedRoomOccupancies({});
        setSplitStayInfo(prev => ({ ...prev, currentSegment: prev.currentSegment + 1, showingIntro: true }));
        setTransitioning(false);
        return;
      }

      const allPrevSegments = [...splitSegmentSelections, segData];
      setSplitSegmentSelections(allPrevSegments);
      setSelectedCheckIn(splitStayInfo.originalCheckIn);
      setSelectedCheckOut(splitStayInfo.originalCheckOut);
      const allRoomIds = allPrevSegments.flatMap(s => s.selectedRoomIds || []);
      const allTempIds = allPrevSegments.flatMap(s => s.temporaryBookingIds || []);
      setSelectedRoomIds(allRoomIds);
      setTemporaryBookingIds(allTempIds);
    }
    // --- FIN SPLIT STAY ---

    setTransitioning(false);
    setStep(3);

  } catch (error) {
    console.error('Erreur création temporary booking:', error);
    setTransitioning(false);
    alert(t.errorOccurred);
  }
};



  // Retour de l'étape 3 à 2 avec suppression des temporary bookings
  const handleBackToRooms = async () => {
    devLog('🔙 Retour à l\'étape 2, libération des chambres...');
      trackStepBack(3, 2, language);

    for (const id of temporaryBookingIds) {
      await deleteTemporaryBooking(id);
    }
    setTemporaryBookingIds([]);
    setSelectedRoomIds([]);
    setStep(2);
  };

  // Calcul du prix d'une chambre
const getRoomPrice = (roomType, occupancy, roomCount = 1) => {
  const nights = selectedCheckIn && selectedCheckOut ? nightsBetween(selectedCheckOut, selectedCheckIn) : 1;
  
  // Utiliser les prix pré-chargés depuis displayPrices
  const pricePerNight = displayPrices[roomType.id] || roomType.basePrice || 0;
  return nights * pricePerNight * roomCount;
};

  // Calcul du nombre total de personnes
  const getTotalGuests = () => {
    let total = 0;
    Object.entries(selectedRoomCount).forEach(([roomTypeId, roomCount]) => {
      if (roomCount > 0) {
        const occupancies = selectedRoomOccupancies[roomTypeId] || [];
        if (occupancies.length > 0) {
          total += occupancies.reduce((sum, occ) => sum + occ, 0);
        } else {
          const defaultOcc = selectedOccupancy[roomTypeId] || 2;
          total += roomCount * defaultOcc;
        }
      }
    });
    return total;
  };

  // Calcul du nombre total de chambres
  const getTotalRooms = () => {
    return Object.values(selectedRoomCount).reduce((sum, count) => sum + (count || 0), 0);
  };

// Récupérer le prix calculé depuis computed_prices
  const getComputedPrice = async (roomTypeId, date) => {
    const dateStr = date.toISOString().split('T')[0];
    const cacheId = `${roomTypeId}_${dateStr}`;
    
    try {
      const priceDoc = await getDoc(doc(db, 'computed_prices', cacheId));
      
      if (priceDoc.exists()) {
        return priceDoc.data().finalPrice;
      } else {
        // Si pas de prix calculé, utiliser le prix de base
        const roomType = roomTypes.find(rt => rt.id === roomTypeId);
        return roomType?.basePrice || 0;
      }
    } catch (error) {
      console.error('Erreur récupération prix calculé:', error);
      const roomType = roomTypes.find(rt => rt.id === roomTypeId);
      return roomType?.basePrice || 0;
    }
  };

// Récupérer les réductions longue durée applicables
  const getLongStayDiscount = async (roomTypeId, checkIn, nights) => {
    try {
      const snapshot = await getDocs(collection(db, 'long_stay_prices'));
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const startDate = data.startDate?.toDate();
        const endDate = data.endDate?.toDate();
        
        // Vérifier si la réservation est dans la période
        if (checkIn >= startDate && checkIn <= endDate) {
          // Vérifier si le nombre de nuits correspond
          if (nights >= data.minNights && nights <= data.maxNights) {
            // Récupérer la réduction pour ce type de chambre
            const reduction = data.variations?.[roomTypeId];
            if (reduction) {
              return {
                name: data.name,
                reduction: Math.abs(reduction), // Valeur positive
                type: data.reductionType
              };
            }
          }
        }
      }
    } catch (error) {
      console.error('Erreur récupération réduction longue durée:', error);
    }
    
    return null;
  };

  // Calcul du nombre total de personnes pour un type spécifique
  const getTotalGuestsForType = (roomTypeId) => {
    const occupancies = selectedRoomOccupancies[roomTypeId] || [];
    return occupancies.reduce((sum, occ) => sum + occ, 0);
  };

  // Calcul du prix pour plusieurs chambres avec occupations différentes
const applyLongStayToTotal = (roomTypeId, pricePerNight, nights, roomCount) => {
  const d = longStayDiscounts[roomTypeId];
  if (!d) return pricePerNight * nights * roomCount;
  // variations[roomTypeId] stocke un delta (ex: 15 = 15€ de réduction par nuit)
  switch (d.type) {
    case 'adjustedNightly':    return (pricePerNight - d.reduction) * nights * roomCount;
    case 'adjustedPercentage': return pricePerNight * (1 - d.reduction / 100) * nights * roomCount;
    case 'weekly':             return (pricePerNight - d.reduction / 7) * nights * roomCount;
    case 'monthly':            return (pricePerNight - d.reduction / 30) * nights * roomCount;
    default:                   return pricePerNight * nights * roomCount;
  }
};

const getRoomPriceForMultipleRooms = (roomType, roomCount) => {
  const nights = selectedCheckIn && selectedCheckOut ? nightsBetween(selectedCheckOut, selectedCheckIn) : 1;
  const pricePerNight = displayPrices[roomType.id] || roomType.basePrice || 0;
  return Math.round(applyLongStayToTotal(roomType.id, pricePerNight, nights, roomCount));
};

  // Calcul du prix des options
const calculateOptionsTotal = useCallback(() => {
    let total = 0;
    const nights = selectedCheckIn && selectedCheckOut ? nightsBetween(selectedCheckOut, selectedCheckIn) : 1;
    
    // ✅ FIX : En mode admin, calculer depuis selectedRoomIds au lieu de selectedRoomCount
    let guests = 0;
    let rooms = 0;
    
    if (adminMode && selectedRoomIds && selectedRoomIds.length > 0) {
      // MODE ADMIN : Calculer depuis selectedRoomIds
      rooms = selectedRoomIds.length;
      guests = selectedRoomIds.reduce((sum, roomId) => {
        return sum + (selectedOccupancy[roomId] || 2);
      }, 0);
    } else {
      // MODE PUBLIC : Utiliser les fonctions existantes
      guests = getTotalGuests();
      rooms = getTotalRooms();
    }
  
    
    selectedOptions.forEach(optionId => {
      const option = loadedOptions.find(o => o.id === optionId);
      
      if (option) {
        let optionTotal = 0;
        switch(option.unit) {
          case 'personne/jour':
            optionTotal = option.price * guests * nights;
            break;
          case 'personne':
            optionTotal = option.price * guests;
            break;
          case 'jour':
            optionTotal = option.price * nights;
            break;
          case 'chambre':
            optionTotal = option.price * rooms;
            break;
          case 'chambre/jour':
            optionTotal = option.price * rooms * nights;
            break;
          case 'réservation':
          case 'unité':
            optionTotal = option.price;
            break;
          default:
            optionTotal = option.price;
            break;
        }
        total += optionTotal;
      } else {
        console.error('❌ Option non trouvée dans loadedOptions:', optionId);
      }
    });
    
    return total;
  }, [selectedOptions, loadedOptions, selectedCheckIn, selectedCheckOut, selectedRoomIds, selectedOccupancy, adminMode, roomTypes]);

// Calcul du total avec coupon
const calculateTotal = useCallback(() => {
    if (!selectedCheckIn || !selectedCheckOut) return 0;

  const nights = nightsBetween(selectedCheckOut, selectedCheckIn);
  let roomTotal = 0;

  if (splitSegmentSelections.length > 0) {
    roomTotal = splitSegmentSelections.reduce((sum, seg) => sum + (seg.segmentRoomTotal || 0), 0);
  } else if (adminMode && selectedRoomIds && selectedRoomIds.length > 0) {
    selectedRoomIds.forEach(roomId => {
      const roomTypeId = selectedOccupancy[roomId + '_type'];
      const roomType = roomTypes.find(rt => rt.id === roomTypeId);

      if (roomType) {
        const pricePerNight = displayPrices[roomType.id] || roomType.basePrice || 0;
        roomTotal += applyLongStayToTotal(roomType.id, pricePerNight, nights, 1);
      }
    });
  } else {
    Object.entries(selectedRoomCount || {}).forEach(([typeId, count]) => {
      if (count > 0) {
        const rt = roomTypes.find(t => t.id === typeId);
        if (rt) {
          const pricePerNight = displayPrices[rt.id] || rt.basePrice || 0;
          roomTotal += applyLongStayToTotal(rt.id, pricePerNight, nights, count);
        }
      }
    });
  }

  const optionsTotal = calculateOptionsTotal();
  let total = roomTotal + optionsTotal;

  if (appliedCoupon) {
    const discount = calculateCouponDiscount(roomTotal);
    total = total - discount;
  }

return total;
}, [selectedCheckIn, selectedCheckOut, selectedRoomIds, selectedOccupancy, roomTypes, displayPrices, longStayDiscounts, selectedRoomCount, appliedCoupon, adminMode, calculateOptionsTotal, splitSegmentSelections]);

  // Fonction pour calculer la réduction du coupon
const calculateCouponDiscount = useCallback((roomTotal) => {
      if (!appliedCoupon) return 0;

    const nights = selectedCheckIn && selectedCheckOut ? nightsBetween(selectedCheckOut, selectedCheckIn) : 1;
    const roomCount = selectedRoomCount[selectedRoomType] || 1;

    switch (appliedCoupon.reductionType) {
      case 'percentage':
        // Pourcentage sur les chambres uniquement
        return roomTotal * (appliedCoupon.reductionAmount / 100);
      
      case 'fixedPerProduct':
        // Réduction fixe par nuit
        return appliedCoupon.reductionAmount * nights * roomCount;
      
      case 'settlementCode':
        // Avoir sur le total (peut rendre le total négatif)
        return appliedCoupon.reductionAmount;
      
      default:
        return 0;
}
  }, [appliedCoupon, selectedCheckIn, selectedCheckOut, selectedRoomCount, selectedRoomType]);

// Fonction pour valider le code coupon
const validateCouponCode = async (codeToValidate = null) => {
    const code = codeToValidate || couponCode;
    
    if (!code || code.trim() === '') {
      setCouponError(language === 'fr' ? 'Veuillez entrer un code' : 'Please enter a code');
      return;
    }

    setValidatingCoupon(true);
    setCouponError('');

    try {
      const couponsQuery = query(
        collection(db, 'coupons'),
        where('code', '==', code.toLowerCase().trim())
      );


      
      const snapshot = await getDocs(couponsQuery);
      
      if (snapshot.empty) {
        setCouponError(language === 'fr' ? 'Code invalide' : 'Invalid code');
        setValidatingCoupon(false);
        return;
      }

      const couponDoc = snapshot.docs[0];
      const coupon = { id: couponDoc.id, ...couponDoc.data() };

      // Vérifier si désactivé
      if (coupon.isDisabled) {
        setCouponError(language === 'fr' ? 'Ce code n\'est pas disponible' : 'This code is not available');
        setValidatingCoupon(false);
        return;
      }

      // Vérifier si déjà utilisé
      if (coupon.isUsed) {
        setCouponError(language === 'fr' ? 'Ce code a déjà été utilisé' : 'This code has already been used');
        setValidatingCoupon(false);
        return;
      }

      // Vérifier la validité (date de début + durée + 7 jours de tolérance)
      const startDate = coupon.startDate?.toDate ? coupon.startDate.toDate() : new Date(coupon.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (coupon.durationDays || 0) + 7); // +7 jours de tolérance

      if (today < startDate) {
        setCouponError(language === 'fr' ? 'Ce code n\'est pas encore valide' : 'This code is not yet valid');
        setValidatingCoupon(false);
        return;
      }

      if (today > endDate) {
        setCouponError(language === 'fr' ? 'Ce code a expiré' : 'This code has expired');
        setValidatingCoupon(false);
        return;
      }



// Code valide, l'appliquer
      setAppliedCoupon(coupon);
      setCouponError('');
      
      // Afficher le modal de succès
      setShowCouponSuccessModal(true);




    } catch (error) {
      console.error('Erreur validation coupon:', error);
      setCouponError(language === 'fr' ? 'Erreur de validation' : 'Validation error');
    } finally {
      setValidatingCoupon(false);
    }
  };

  // Fonction pour retirer le coupon appliqué
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setCouponError('');
  };

// Clic sur "Payer" dans PaymentStep → bascule le doc en payment_abandoned
const handlePaymentAttemptStarted = async () => {
  paymentAttemptedRef.current = true;
  try {
    await setDoc(doc(db, 'lost_reservations', sessionId), {
      trackingId: bookingTrackingId,
      sessionId: sessionId,
      clientIP: userIP || null,
      roomTypeId: selectedRoomType,
      checkIn: Timestamp.fromDate(selectedCheckIn),
      checkOut: Timestamp.fromDate(selectedCheckOut),
      abandonStage: 'payment_abandoned',
      language: language || null,
      guestInfo: {
        name: savedGuestInfoRef.current.name || '',
        firstName: savedGuestInfoRef.current.firstName || '',
        email: savedGuestInfoRef.current.email || '',
        phone: savedGuestInfoRef.current.phone || ''
      },
      createdAt: Timestamp.now(),
      abandonedAt: Timestamp.now()
    });
    devLog('📊 Tentative paiement démarrée — payment_abandoned pré-enregistré');
  } catch (error) {
    console.error('Erreur tracking payment_abandoned:', error);
  }
};

// Gestion du succès du paiement
const handlePaymentSuccess = async (paymentData) => {
  devLog('✅ Paiement autorisé:', paymentData);
  // Supprimer l'entrée d'abandon pré-enregistrée (résa finalisée = pas un abandon)
  deleteDoc(doc(db, 'lost_reservations', sessionId)).catch(() => {});
  setPaymentIntent(paymentData);
  setPaymentAttempts(0);

  // 🔒 Option B : écrire toutes les données en Firestore et ATTENDRE avant handleSubmitWithPayment
  // Si le navigateur ferme ici (3D Secure OK mais JS pas terminé), le CRON crée la résa et envoie l'email au client
  if (paymentData?.setupIntentId) {
    const nights = nightsBetween(selectedCheckOut, selectedCheckIn);
    let roomTotalForDoc = 0;
    Object.entries(selectedRoomCount).forEach(([roomTypeId, count]) => {
      if (!count) return;
      const rt = roomTypes.find(t => t.id === roomTypeId);
      if (rt) {
        const pricePerNight = displayPrices[rt.id] || rt.basePrice || 0;
        roomTotalForDoc += nights * pricePerNight * count;
      }
    });

    try {
      await setDoc(doc(db, 'pending_payment', paymentData.setupIntentId), {
        setupIntentId: paymentData.setupIntentId,
        stripeCustomerId: paymentData.stripeCustomerId,
        stripePaymentMethodId: paymentData.stripePaymentMethodId,
        cardholderName: paymentData.cardholderName,
        last4: paymentData.last4,
        brand: paymentData.brand,
        expiryMonth: paymentData.expiryMonth || null,
        expiryYear: paymentData.expiryYear || null,
        guestInfo: {
          name: guestInfo.name,
          firstName: guestInfo.firstName,
          email: guestInfo.email,
          phone: guestInfo.phone,
          company: guestInfo.company || '',
          address: guestInfo.address || '',
          city: guestInfo.city || '',
          postalCode: guestInfo.postalCode || '',
          country: guestInfo.country || 'France',
          message: guestInfo.message || ''
        },
        checkIn: selectedCheckIn.toISOString(),
        checkOut: selectedCheckOut.toISOString(),
        selectedRoomType: selectedRoomType,
        selectedRoomIds: [...(selectedRoomIds || [])],
        selectedRoomCount: { ...selectedRoomCount },
        selectedOccupancy: { ...selectedOccupancy },
        selectedRoomOccupancies: { ...selectedRoomOccupancies },
        displayPrices: { ...displayPrices },
        selectedOptions: [...(selectedOptions || [])],
        appliedCoupon: appliedCoupon ? {
          code: appliedCoupon.code,
          description: appliedCoupon.description || '',
          reductionType: appliedCoupon.reductionType || '',
          reductionAmount: appliedCoupon.reductionAmount || 0
        } : null,
        roomTotal: roomTotalForDoc,
        optionsTotal: calculateOptionsTotal(),
        total: calculateTotal(),
        language: language,
        customAnswers: customAnswers || {},
        guestsDetails: guestsDetails || [],
        sessionId: sessionId,
        temporaryBookingIds: [...(temporaryBookingIds || [])],
        ...(splitSegmentSelections.length > 0 ? {
          isSplitStay: true,
          splitSegments: splitSegmentSelections.map(seg => ({
            checkIn: seg.checkIn instanceof Date ? seg.checkIn.toISOString() : seg.checkIn,
            checkOut: seg.checkOut instanceof Date ? seg.checkOut.toISOString() : seg.checkOut,
            selectedRoomIds: seg.selectedRoomIds || [],
            selectedRoomCount: seg.selectedRoomCount || {},
            selectedOccupancy: seg.selectedOccupancy || {},
            selectedRoomOccupancies: seg.selectedRoomOccupancies || {},
            displayPrices: seg.displayPrices || {},
            segmentRoomTotal: seg.segmentRoomTotal || 0,
          }))
        } : {}),
        createdAt: Timestamp.now()
      });
      console.log('✅ pending_payment écrit en Firestore:', paymentData.setupIntentId);
    } catch (err) {
      console.warn('⚠️ pending_payment non écrit (non bloquant):', err);
    }
  } else {
    console.warn('⚠️ handlePaymentSuccess : setupIntentId absent dans paymentData', paymentData);
  }

  handleSubmitWithPayment(paymentData);
};

// Gestion de l'échec du paiement
const handlePaymentError = async () => {
  const newAttempts = paymentAttempts + 1;
  setPaymentAttempts(newAttempts);
  trackPaymentFailed(`Tentative ${newAttempts}/3 échouée`, language);
  
  devLog(`❌ Tentative ${newAttempts}/3 échouée`);
  
  if (newAttempts >= 3) {
    // Après 3 tentatives, annuler la réservation
    if (createdReservationId) {
      try {
        await updateDoc(doc(db, 'reservations', createdReservationId), {
          status: 'cancelled',
          'payment.status': 'failed',
          'payment.failureReason': 'Carte refusée après 3 tentatives',
          cancelledAt: Timestamp.now(),
          cancelledReason: 'Échec paiement après 3 tentatives'
        });
        devLog('❌ Réservation annulée après 3 tentatives');
      } catch (error) {
        console.error('Erreur annulation réservation:', error);
      }
      
      // Libérer les temporary bookings
      for (const id of temporaryBookingIds) {
        await deleteTemporaryBooking(id);
      }
}

// Afficher le modal d'échec
trackReservationCancelled('payment_failed_3_attempts', language);
setShowPaymentFailedModal(true);
  }
};

  // Soumission de la réservation avec paiement
// Soumission de la réservation avec paiement
// ════════════════════════════════════════════════════════════════════
// FONCTION handleSubmitWithPayment COMPLÈTE - Prête à copier/coller
// Supprimer l'ancienne fonction (lignes 1925-2492) et remplacer par celle-ci
// ════════════════════════════════════════════════════════════════════

const handleSubmitWithPayment = async (paymentData) => {
  if (!selectedCheckIn || !selectedCheckOut || !selectedRoomType || 
      !guestInfo.name || !guestInfo.firstName || !guestInfo.email || !guestInfo.phone ||
      !guestInfo.address || !guestInfo.city || !guestInfo.postalCode) {
    alert(t.allFieldsRequired);
    return;
  }

  if (paymentData && (!paymentData.last4 || !paymentData.brand)) {
    console.error('❌ Données de carte incomplètes:', paymentData);
    alert(t.errorOccurred);
    return;
  }

  // 🎯 AFFICHER LE MODAL DE CHARGEMENT IMMÉDIATEMENT
  setLoadingModal(true);
  setSubmitting(true);
  bookingStartedRef.current = false;
  
  try {
    devLog('🚀 Création de la réservation...');

    const freshReservations = await loadReservationsForMonth(currentMonth);
    const freshTempBookings = await loadAndCleanTemporaryBookings();

    // Pour les split stays, lookup roomId → dates du segment correspondant
    const roomSegmentDates = {};
    if (splitSegmentSelections.length > 0) {
      splitSegmentSelections.forEach(seg => {
        (seg.selectedRoomIds || []).forEach(rid => {
          roomSegmentDates[rid] = { checkIn: seg.checkIn, checkOut: seg.checkOut };
        });
      });
    }
    const getRoomDates = (roomId) => roomSegmentDates[roomId] || { checkIn: selectedCheckIn, checkOut: selectedCheckOut };

    const occupancy = selectedOccupancy[selectedRoomType] || 2;
    const roomCount = selectedRoomCount[selectedRoomType] || 1;

    const roomType = roomTypes.find(rt => rt.id === selectedRoomType);
    const roomsOfType = roomType?.assignedRooms ?
      rooms.filter(r => roomType.assignedRooms.includes(r.id)) : [];
    const finalAvailableRoomIds = [];

    if (splitSegmentSelections.length > 0) {
      // Split stay : vérifier chaque chambre par segment
      for (const seg of splitSegmentSelections) {
        const segCI = seg.checkIn instanceof Date ? seg.checkIn : new Date(seg.checkIn);
        const segCO = seg.checkOut instanceof Date ? seg.checkOut : new Date(seg.checkOut);
        for (const roomId of (seg.selectedRoomIds || [])) {
          const hasConflict = freshReservations.some(res => {
            if (res.status === 'cancelled') return false;
            const isRoomInRes = res.rooms ? res.rooms.some(r => r.roomId === roomId) : res.roomId === roomId;
            if (!isRoomInRes) return false;
            const resCI = res._checkInNorm || normalizeToParisDate(res.checkIn);
            const resCO = res._checkOutNorm || normalizeToParisDate(res.checkOut);
            return segCI < resCO && segCO > resCI;
          });
          const hasTempConflict = freshTempBookings.some(t =>
            t.roomId === roomId && t.sessionId !== sessionId && segCI < t.checkOut && segCO > t.checkIn
          );
          if (!hasConflict && !hasTempConflict) finalAvailableRoomIds.push(roomId);
        }
      }
      const totalNeeded = splitSegmentSelections.reduce((sum, seg) =>
        sum + Object.values(seg.selectedRoomCount || {}).reduce((a, b) => a + b, 0), 0);
      if (finalAvailableRoomIds.length < totalNeeded) {
        // Chambre partie pendant que le client finalisait sa réservation.
        // Sa ligne d'abandon vient d'être supprimée (le code considérait la réservation
        // faite dès la carte acceptée) et bookingStartedRef est retombé à false : sans cette
        // réécriture, ce client n'apparaîtrait dans AUCUNE tuile d'abandon.
        try {
          await setDoc(doc(db, 'lost_reservations', sessionId), {
            trackingId: bookingTrackingId,
            sessionId: sessionId,
            clientIP: userIP || null,
            roomTypeId: selectedRoomType,
            checkIn: Timestamp.fromDate(selectedCheckIn),
            checkOut: Timestamp.fromDate(selectedCheckOut),
            abandonStage: 'payment_abandoned',
            roomLostAtPayment: true,
            language: language || null,
            guestInfo: {
              name: guestInfo.name || '',
              firstName: guestInfo.firstName || '',
              email: guestInfo.email || '',
              phone: guestInfo.phone || ''
            },
            createdAt: Timestamp.now(),
            abandonedAt: Timestamp.now()
          });
        } catch (err) {
          console.error('Erreur tracking chambre perdue au paiement:', err);
        }
        alert(t.roomsNoLongerAvailable);
        for (const id of temporaryBookingIds) { await deleteTemporaryBooking(id); }
        setLoadingModal(false);
        setSubmitting(false);
        setStep(2);
        return;
      }
    } else {
      // Standard : vérifier disponibilité des chambres
      for (const roomId of selectedRoomIds) {
        const room = rooms.find(r => r.id === roomId);
        const roomCapacity = room?.maxOccupancy || roomType?.maxOccupancy || roomType?.maxGuests || 2;
        const requiredOccupancy = selectedOccupancy[selectedRoomType] || 2;

        if (roomCapacity < requiredOccupancy) continue;

        const { checkIn: roomCI, checkOut: roomCO } = getRoomDates(roomId);
        const hasConflict = freshReservations.some(res => {
          if (res.status === 'cancelled') return false;

          let isRoomInReservation = false;
          if (res.rooms && Array.isArray(res.rooms)) {
            isRoomInReservation = res.rooms.some(r => r.roomId === roomId);
          } else if (res.roomId === roomId) {
            isRoomInReservation = true;
          }

          if (!isRoomInReservation) return false;

          const resCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
          const resCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);
          return (roomCI < resCheckOut && roomCO > resCheckIn);
        });

        const hasTempConflict = freshTempBookings.some(temp =>
          temp.roomId === roomId &&
          temp.sessionId !== sessionId &&
          roomCI < temp.checkOut &&
          roomCO > temp.checkIn
        );

        if (!hasConflict && !hasTempConflict) {
          finalAvailableRoomIds.push(roomId);
        }
      }

      // Rechercher des chambres supplémentaires si nécessaire
      if (finalAvailableRoomIds.length < roomCount) {
        const requiredOccupancy = selectedOccupancy[selectedRoomType] || 2;

        for (const room of roomsOfType) {
          if (finalAvailableRoomIds.includes(room.id)) continue;

          const roomCapacity = room.maxOccupancy || roomType?.maxOccupancy || roomType?.maxGuests || 2;
          if (roomCapacity < requiredOccupancy) continue;

          const { checkIn: roomCI, checkOut: roomCO } = getRoomDates(room.id);
          const hasConflict = freshReservations.some(res => {
            if (res.status === 'cancelled') return false;

            let isRoomInReservation = false;
            if (res.rooms && Array.isArray(res.rooms)) {
              isRoomInReservation = res.rooms.some(r => r.roomId === room.id);
            } else if (res.roomId === room.id) {
              isRoomInReservation = true;
            }

            if (!isRoomInReservation) return false;

            const resCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
            const resCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);
            return (roomCI < resCheckOut && roomCO > resCheckIn);
          });

          const hasTempConflict = freshTempBookings.some(temp =>
            temp.roomId === room.id &&
            temp.sessionId !== sessionId &&
            roomCI < temp.checkOut &&
            roomCO > temp.checkIn
          );

          if (!hasConflict && !hasTempConflict) {
            finalAvailableRoomIds.push(room.id);
            if (finalAvailableRoomIds.length >= roomCount) break;
          }
        }
      }

      if (finalAvailableRoomIds.length < roomCount) {
        // Chambre partie pendant que le client finalisait sa réservation.
        // Sa ligne d'abandon vient d'être supprimée (le code considérait la réservation
        // faite dès la carte acceptée) et bookingStartedRef est retombé à false : sans cette
        // réécriture, ce client n'apparaîtrait dans AUCUNE tuile d'abandon.
        try {
          await setDoc(doc(db, 'lost_reservations', sessionId), {
            trackingId: bookingTrackingId,
            sessionId: sessionId,
            clientIP: userIP || null,
            roomTypeId: selectedRoomType,
            checkIn: Timestamp.fromDate(selectedCheckIn),
            checkOut: Timestamp.fromDate(selectedCheckOut),
            abandonStage: 'payment_abandoned',
            roomLostAtPayment: true,
            language: language || null,
            guestInfo: {
              name: guestInfo.name || '',
              firstName: guestInfo.firstName || '',
              email: guestInfo.email || '',
              phone: guestInfo.phone || ''
            },
            createdAt: Timestamp.now(),
            abandonedAt: Timestamp.now()
          });
        } catch (err) {
          console.error('Erreur tracking chambre perdue au paiement:', err);
        }
        alert(t.roomsNoLongerAvailable);

        for (const id of temporaryBookingIds) {
          await deleteTemporaryBooking(id);
        }

        setLoadingModal(false);
        setSubmitting(false);
        setStep(2);
        return;
      }
    }

    let roomTotal = 0;
    if (splitSegmentSelections.length > 0) {
      roomTotal = splitSegmentSelections.reduce((sum, seg) => sum + (seg.segmentRoomTotal || 0), 0);
    } else {
      Object.entries(selectedRoomCount).forEach(([roomTypeId, count]) => {
        if (count === 0) return;
        const rt = roomTypes.find(t => t.id === roomTypeId);
        if (rt) {
          const nights = nightsBetween(selectedCheckOut, selectedCheckIn);
          const pricePerNight = displayPrices[rt.id] || rt.basePrice || 0;
          roomTotal += nights * pricePerNight * count;
        }
      });
    }

    const optionsTotal = calculateOptionsTotal();
    
    devLog(`💰 Total chambres: ${roomTotal}€, Options: ${optionsTotal}€`);
    
    const reservationNumber = await generateUniqueReservationNumber();
    
    devLog('🔍 guestInfo avant findOrCreateClient:', guestInfo);

    // 🔵 BASCULE (2026-07-08) : la réconciliation client (recherche/création de fiche) est
    //    désormais faite CÔTÉ SERVEUR (VPS — veilleur client-linker). La page NE touche plus la
    //    base client → la résa est créée avec clientId = null, et le serveur la rattache ensuite
    //    (lien + stats + accompagnants, ~1 s après). Rollback = fichier .bak-bascule-20260708.
    const clientId = null;
    const clientSyncFailed = false;
    devLog('✅ Client:', clientId, '| syncFailed:', clientSyncFailed);
    
    // ✅ OPTIMISÉ: Questions personnalisées en une seule requête
    const formattedCustomAnswers = await (async () => {
      if (!customAnswers || Object.keys(customAnswers).length === 0) return [];
      
      const questionIds = Object.keys(customAnswers).filter(id => customAnswers[id]?.trim());
      if (questionIds.length === 0) return [];
      
      const questionsSnapshot = await getDocs(
        query(collection(db, 'custom_questions'), where('__name__', 'in', questionIds))
      );
      
      const questionsMap = {};
      questionsSnapshot.forEach(doc => {
        questionsMap[doc.id] = doc.data();
      });
      
      return questionIds.map(questionId => {
        const questionData = questionsMap[questionId];
        if (!questionData) return null;
        
        const questionText = language === 'fr' ? 
          questionData.questionFr : 
          (questionData.questionEn || questionData.questionFr);
        
        return {
          questionId,
          question: questionText,
          answer: customAnswers[questionId]
        };
      }).filter(Boolean);
    })();

    const roomsArray = [];

    if (splitSegmentSelections.length > 0) {
      // Split stay : construire roomsArray depuis tous les segments
      for (const seg of splitSegmentSelections) {
        const segCI = seg.checkIn instanceof Date ? seg.checkIn : new Date(seg.checkIn);
        const segCO = seg.checkOut instanceof Date ? seg.checkOut : new Date(seg.checkOut);
        let segRoomIndex = 0;
        Object.entries(seg.selectedRoomCount || {}).forEach(([roomTypeId, count]) => {
          if (!count) return;
          const rt = roomTypes.find(t => t.id === roomTypeId);
          if (!rt) return;
          const occupancies = seg.selectedRoomOccupancies?.[roomTypeId] || [];
          const priceTTC = seg.displayPrices?.[roomTypeId] || rt.basePrice || 0;
          const vatRate = rt.vatRate ?? 0;
          const priceHT = priceTTC / (1 + vatRate / 100);
          const vatAmount = priceTTC - priceHT;
          for (let i = 0; i < count; i++) {
            const roomId = (seg.selectedRoomIds || [])[segRoomIndex];
            if (!roomId) { segRoomIndex++; return; }
            const room = rooms.find(r => r.id === roomId);
            const roomOccupancy = occupancies[i] || seg.selectedOccupancy?.[roomTypeId] || 2;
            roomsArray.push({
              roomId,
              roomNumber: room?.roomNumber || room?.number || '',
              roomType: rt.nameFr || rt.name || '',
              roomTypeId,
              occupancy: roomOccupancy,
              pricePerNight: priceTTC,
              vatRate,
              priceHT,
              priceTTC,
              vatAmount,
              segmentCheckIn: Timestamp.fromDate(segCI),
              segmentCheckOut: Timestamp.fromDate(segCO),
            });
            segRoomIndex++;
          }
        });
      }
    } else {
      let roomIndex = 0;
      Object.entries(selectedRoomCount).forEach(([roomTypeId, count]) => {
        if (count === 0) return;

        const rt = roomTypes.find(t => t.id === roomTypeId);
        if (!rt) return;

        const occupancies = selectedRoomOccupancies[roomTypeId] || [];

        for (let i = 0; i < count; i++) {
          const roomId = finalAvailableRoomIds[roomIndex];
          if (!roomId) {
            console.error(`❌ Pas de roomId pour index ${roomIndex}`);
            continue;
          }

          const room = rooms.find(r => r.id === roomId);
          const roomOccupancy = occupancies[i] || selectedOccupancy[roomTypeId] || 2;

          const priceTTC = displayPrices[rt.id] || rt.basePrice;
          const vatRate = rt.vatRate ?? 0;
          const priceHT = priceTTC / (1 + vatRate / 100);
          const vatAmount = priceTTC - priceHT;

          roomsArray.push({
            roomId: roomId,
            roomNumber: room?.roomNumber || room?.number || '',
            roomType: rt.nameFr || rt.name || '',
            roomTypeId: roomTypeId,
            occupancy: roomOccupancy,
            pricePerNight: priceTTC,
            vatRate: vatRate,
            priceHT: priceHT,
            priceTTC: priceTTC,
            vatAmount: vatAmount
          });

          roomIndex++;
        }
      });
    }

    devLog(`✅ ${roomsArray.length} chambres dans la réservation:`, 
      roomsArray.map(r => `${r.roomNumber} (${r.roomType})`).join(', '));

    if (roomsArray.length === 0) {
      console.error('❌ ERREUR: Aucune chambre dans roomsArray !');
      alert(t.errorOccurred);
      setLoadingModal(false);
      setSubmitting(false);
      return;
    }

    const reservation = {
      number: reservationNumber,
      rooms: roomsArray,
      checkIn: Timestamp.fromDate(selectedCheckIn),
      checkOut: Timestamp.fromDate(selectedCheckOut),
      language: language,
      clientId: clientId,
      clientSyncFailed: clientSyncFailed,
      couponApplied: appliedCoupon ? {
        code: appliedCoupon.code,
        description: appliedCoupon.description,
        reductionType: appliedCoupon.reductionType,
        reductionAmount: appliedCoupon.reductionAmount,
        discountCalculated: calculateCouponDiscount(roomTotal),
        appliedAt: Timestamp.now()
      } : null,
      client: {
        name: guestInfo.name,
        firstName: guestInfo.firstName,
        company: guestInfo.company,
        email: guestInfo.email,
        phone: guestInfo.phone,
        address: guestInfo.address,
        city: guestInfo.city,
        postalCode: guestInfo.postalCode,
        country: guestInfo.country
      },
      guests: {
        total: nombreDePersonnes(roomsArray),
        adults: nombreDePersonnes(roomsArray),
        children: 0
      },
      pricing: {
        total: roomTotal + optionsTotal,
        roomTotal: roomTotal,
        optionsTotal: optionsTotal,
        paid: 0,
        roomDetails: roomsArray.map(room => {
          const segCI = room.segmentCheckIn ? room.segmentCheckIn.toDate() : selectedCheckIn;
          const segCO = room.segmentCheckOut ? room.segmentCheckOut.toDate() : selectedCheckOut;
          return {
            roomId: room.roomId,
            pricePerNight: room.pricePerNight,
            totalPrice: room.pricePerNight * nightsBetween(segCO, segCI)
          };
        })
      },
      options: [],
      consumptions: selectedOptions.map(optionId => {
        const option = loadedOptions.find(o => o.id === optionId);
        const nights = nightsBetween(selectedCheckOut, selectedCheckIn);
        const totalGuests = getTotalGuests();
        const vatRate = option.vatRate || 0;
        const priceIncludesVat = option.priceIncludesVat !== false;
        
        let quantity = 1;
        let totalPrice = option.price;
        
        switch(option.unit) {
          case 'personne/jour':
            quantity = totalGuests * nights;
            totalPrice = option.price * quantity;
            break;
          case 'personne':
            quantity = totalGuests;
            totalPrice = option.price * quantity;
            break;
          case 'jour':
            quantity = nights;
            totalPrice = option.price * quantity;
            break;
          case 'chambre':
            quantity = roomCount;
            totalPrice = option.price * quantity;
            break;
          case 'chambre/jour':
            quantity = roomCount * nights;
            totalPrice = option.price * quantity;
            break;
          case 'réservation':
          case 'unité':
          default:
            quantity = 1;
            totalPrice = option.price;
            break;
        }
        
        const totalHT = priceIncludesVat ? 
          (totalPrice / (1 + vatRate / 100)) : 
          totalPrice;
        const totalTTC = priceIncludesVat ? 
          totalPrice : 
          (totalPrice * (1 + vatRate / 100));
        const totalVAT = totalTTC - totalHT;
        
        const description = language === 'fr' ? 
          (option.nameFr || option.name) : 
          option.name;
        
        return {
          id: crypto.randomUUID(),
          type: option.category || 'other',
          isOption: true,
          optionId: option.id,
          description: description,
          quantity: quantity,
          unitPrice: option.price,
          totalPrice: totalPrice,
          unit: option.unit,
          date: selectedCheckIn.toISOString(),
          status: 'pending',
          vatRate: vatRate,
          priceIncludesVat: priceIncludesVat,
          unitPriceHT: Math.round((priceIncludesVat ? option.price / (1 + vatRate / 100) : option.price) * 100) / 100,
          unitPriceTTC: Math.round((priceIncludesVat ? option.price : option.price * (1 + vatRate / 100)) * 100) / 100,
          totalHT: Math.round(totalHT * 100) / 100,
          totalTTC: Math.round(totalTTC * 100) / 100,
          totalVAT: Math.round(totalVAT * 100) / 100
        };
      }),
      status: 'pending',
      payment: {
        method: 'stripe',
        status: paymentData ? 'authorized' : 'awaiting_card',
        notes: paymentData ? 'Carte enregistrée' : 'En attente d\'enregistrement de carte bancaire'
      },
      source: 'website',
      notes: guestInfo.message,
      customAnswers: formattedCustomAnswers,
      guestsDetails: guestsDetails.length > 0 ? guestsDetails : undefined,
      guestsCompleted: guestsDetails.length > 1 && guestsDetails.every((g, i) => i === 0 || (g.firstName && g.lastName)),
      guestsCompletedAt: (guestsDetails.length > 1 && guestsDetails.every((g, i) => i === 0 || (g.firstName && g.lastName))) ? Timestamp.now() : null,
      guestsCompletedBy: (guestsDetails.length > 1 && guestsDetails.every((g, i) => i === 0 || (g.firstName && g.lastName))) ? 'client' : null,
      createdAt: Timestamp.now(),
      createdBy: 'Public Booking',
      
      cgvAcceptance: !adminMode && (cgvLinks[language] || cgvTexts[language]) ? {
        accepted: cgvAccepted,
        acceptedAt: Timestamp.now(),
        ipAddress: userIP || null,
        userAgent: navigator.userAgent || null,
        cgvUrl: cgvLinks[language] || null,
        cgvTextUsed: cgvUseText && cgvTexts[language] ? true : false
      } : null,
      ...(splitSegmentSelections.length > 0 ? {
        isSplitStay: true,
        splitSegments: splitSegmentSelections.map(seg => ({
          checkIn: seg.checkIn instanceof Date ? seg.checkIn.toISOString() : seg.checkIn,
          checkOut: seg.checkOut instanceof Date ? seg.checkOut.toISOString() : seg.checkOut,
          roomIds: seg.selectedRoomIds || [],
        }))
      } : {})
    };

    if (paymentData) {
      reservation.stripeCustomerId = paymentData.stripeCustomerId;
      reservation.stripeCards = [{
        id: paymentData.stripePaymentMethodId,
       cardholderName: paymentData.cardholderName,
        last4: paymentData.last4,
        brand: paymentData.brand,
        exp_month: paymentData.expiryMonth || 12,
        exp_year: paymentData.expiryYear || 2030,
        addedAt: Timestamp.now(),
        isDefault: true
      }];
      reservation.payment.stripeCustomerId = paymentData.stripeCustomerId;
      reservation.payment.stripePaymentMethodId = paymentData.stripePaymentMethodId;
      reservation.payment.stripeSetupIntentId = paymentData.setupIntentId;
      reservation.payment.cardholderName = paymentData.cardholderName;
      reservation.payment.last4 = paymentData.last4;
      reservation.payment.brand = paymentData.brand;
      reservation.payment.authorizedAmount = roomTotal + optionsTotal;
      reservation.payment.authorizedAt = Timestamp.now();
    }

    // 🎯 CRÉER LA RÉSERVATION
    const reservationId = await createReservation(reservation);
    devLog('✅ Réservation créée:', reservationId);

    // ✅ Supprimer le doc pending_payment dès que la réservation est en base (plus besoin du CRON pour récupérer)
    if (paymentData?.setupIntentId) {
      deleteDoc(doc(db, 'pending_payment', paymentData.setupIntentId)).catch(() => {});
    }

    setCreatedReservationId(reservationId);

    // 🚀 TOUT EN PARALLÈLE
    devLog('🔄 Exécution des tâches en parallèle...');
    
    await Promise.allSettled([
      // Logs
      logReservationCreated(
        reservationId,
        'client',
        `${guestInfo.firstName} ${guestInfo.name}`,
        {
          source: adminMode ? 'admin' : 'website',
          totalAmount: roomTotal + optionsTotal,
          roomCount: roomCount
        }
      ),
      
      // Log CGR
      (!adminMode && cgvAccepted && (cgvLinks[language] || cgvTexts[language])) ?
        logCGRAccepted(reservationId, {
          ipAddress: userIP || null,
          userAgent: navigator.userAgent || null,
          cgvUrl: cgvLinks[language] || null,
          cgvTextUsed: cgvUseText && cgvTexts[language] ? true : false
        }) : Promise.resolve(),
      
      // Notification admin
      addDoc(collection(db, 'adminNotifications'), {
        type: 'new_reservation',
        reservationId: reservationId,
        reservationNumber: reservationNumber,
        clientName: `${guestInfo.firstName} ${guestInfo.name}`,
        checkIn: format(selectedCheckIn, 'dd/MM/yyyy'),
        checkOut: format(selectedCheckOut, 'dd/MM/yyyy'),
        createdAt: Timestamp.now(),
        read: false
      }),
      
      // Envoi emails
      (async () => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://valetier.maisonceronne.com';
        
        const templatesSnapshot = await getDocs(
          query(
            collection(db, 'email_templates'),
            where('automation', 'in', ['immediate_booking_request', 'immediate_booking_request_admin'])
          )
        );
        
        if (!templatesSnapshot.empty) {
          devLog(`📧 Envoi de ${templatesSnapshot.size} email(s)...`);
          
          for (const templateDoc of templatesSnapshot.docs) {
            try {
              const response = await fetch(`${backendUrl}/api/email/send-reservation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  reservationId: reservationId,
                  templateId: templateDoc.id,
                  language: language || 'fr',
                  sentBy: 'SYSTEM'
                })
              });
              
              const result = await response.json();
              if (result.success) {
                devLog(`✅ Email ${templateDoc.data().name} envoyé`);
              }
            } catch (emailError) {
              console.error(`Erreur envoi email ${templateDoc.id}:`, emailError);
            }
          }
        }
      })(),
      
      // Notification quotidienne
      addDailyNotification({
        type: 'new',
        category: 'room',
        data: {
          reservationNumber: reservationNumber,
          reservationId: reservationId,
          clientName: `${guestInfo.firstName} ${guestInfo.name}`,
          email: guestInfo.email,
          checkIn: format(selectedCheckIn, 'dd/MM/yyyy'),
          checkOut: format(selectedCheckOut, 'dd/MM/yyyy'),
          roomNumber: roomsArray[0]?.roomNumber || '',
          roomType: roomsArray[0]?.roomType || '',
          price: roomTotal + optionsTotal
        }
      }),
      
      // 🔵 BASCULE (2026-07-08) : stats client + accompagnants faits CÔTÉ SERVEUR (VPS)
      //    après rattachement. Plus aucun appel à la base client ici (clientId = null).
      
      // Coupon
      appliedCoupon ? (async () => {
        await updateDoc(doc(db, 'coupons', appliedCoupon.id), {
          isUsed: true,
          usedAt: Timestamp.now(),
          usedByReservation: reservationId
        });

        // LA NOTE FISCALE DU BON. Sans elle, la nuit payée avec un bon cadeau
        // n'était déclarée NULLE PART : la vente du bon n'est pas taxable (bon
        // à usages multiples, art. 256 ter CGI), et son usage ne produisait
        // rien. Le serveur ne note que les bons ACHETÉS — un code issu d'une
        // annulation a déjà sa note, faite quand l'argent est rentré.
        // ⚠️ Cette page existe en DEUX exemplaires : ici et dans VALETIER.
        //    Toute correction doit être reportée sur l'autre.
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/notes-caisse/bon-utilise`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reservationId, couponCode: appliedCoupon.code }),
          });
        } catch (e) {
          // Une note manquée ne doit jamais faire échouer la réservation.
          console.error('Note du bon cadeau :', e);
        }

        await logCouponApplied(
          reservationId,
          'public-booking',
          guestInfo.name || 'Client',
          {
            code: appliedCoupon.code,
            discount: `${appliedCoupon.reductionType === 'percentage' ? appliedCoupon.reductionAmount + '%' : 
                       appliedCoupon.reductionType === 'fixedPerProduct' ? appliedCoupon.reductionAmount + '€/nuit' :
                       appliedCoupon.reductionAmount + '€'}`
          }
        );
      })() : Promise.resolve()
    ]);
    
    devLog('✅ Toutes les tâches parallèles terminées');
    
    // Supprimer les temporary bookings
    devLog('✅ Suppression des temporary bookings...');
    for (const id of temporaryBookingIds) {
      await deleteTemporaryBooking(id);
    }
    
// 🎯 FERMER LE MODAL DE CHARGEMENT
setLoadingModal(false);

// ✅ TRACKER SUCCÈS PAIEMENT
trackPaymentSuccess(reservationId, roomTotal + optionsTotal, language);

// 🎯 AFFICHER LA CONFIRMATION
setConfirmationModal({


      roomCount: roomCount,
      reservationNumber: reservationNumber
    });
    if (paymentData?.setupIntentId) deleteDoc(doc(db, 'payment_safety', paymentData.setupIntentId)).catch(() => {});

  } catch (error) {
    console.error('❌ Erreur création réservation:', error);
    setLoadingModal(false);
    alert(t.errorOccurred);
  } finally {
    setSubmitting(false);
  }
};

// Soumission sans paiement (mode admin)
  const handleSubmitWithoutPayment = async () => {
    if (!selectedCheckIn || !selectedCheckOut || !selectedRoomType || 
        !guestInfo.name || !guestInfo.firstName || !guestInfo.email || !guestInfo.phone ||
        !guestInfo.address || !guestInfo.city || !guestInfo.postalCode) {
      alert(t.allFieldsRequired);
      return;
    }

    setSubmitting(true);
    
// Marquer que la réservation est finalisée (ne pas tracker l'abandon)
    bookingStartedRef.current = false;

    try {
      devLog('🚀 Soumission de la réservation (admin)...');
      
      const freshReservations = await loadReservationsForMonth(currentMonth);
      const freshTempBookings = await loadAndCleanTemporaryBookings();
      
      const occupancy = selectedOccupancy[selectedRoomType] || 2;
      const roomCount = selectedRoomCount[selectedRoomType] || 1;
      
      const roomType = roomTypes.find(rt => rt.id === selectedRoomType);
      const roomsOfType = roomType?.assignedRooms ? 
        rooms.filter(r => roomType.assignedRooms.includes(r.id)) : [];
      const finalAvailableRoomIds = [];
      
      for (const roomId of selectedRoomIds) {
        const room = rooms.find(r => r.id === roomId);
        const roomCapacity = room?.maxOccupancy || roomType?.maxOccupancy || roomType?.maxGuests || 2;
        const requiredOccupancy = selectedOccupancy[selectedRoomType] || 2;
        
        if (roomCapacity < requiredOccupancy) continue;
        
        const hasConflict = freshReservations.some(res => {
          if (res.status === 'cancelled') return false;
          
          let isRoomInReservation = false;
          if (res.rooms && Array.isArray(res.rooms)) {
            isRoomInReservation = res.rooms.some(r => r.roomId === roomId);
          } else if (res.roomId === roomId) {
            isRoomInReservation = true;
          }
          
          if (!isRoomInReservation) return false;
          
          const resCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
          const resCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);
          return (selectedCheckIn < resCheckOut && selectedCheckOut > resCheckIn);
        });

        const hasTempConflict = freshTempBookings.some(temp =>
          temp.roomId === roomId &&
          temp.sessionId !== sessionId &&
          selectedCheckIn < temp.checkOut &&
          selectedCheckOut > temp.checkIn
        );

        if (!hasConflict && !hasTempConflict) {
          finalAvailableRoomIds.push(roomId);
        }
      }
      
      if (finalAvailableRoomIds.length < roomCount) {
        const requiredOccupancy = selectedOccupancy[selectedRoomType] || 2;
        
        for (const room of roomsOfType) {
          if (finalAvailableRoomIds.includes(room.id)) continue;
          
          const roomCapacity = room.maxOccupancy || roomType?.maxOccupancy || roomType?.maxGuests || 2;
          if (roomCapacity < requiredOccupancy) continue;
          
          const hasConflict = freshReservations.some(res => {
            if (res.status === 'cancelled') return false;
            
            let isRoomInReservation = false;
            if (res.rooms && Array.isArray(res.rooms)) {
              isRoomInReservation = res.rooms.some(r => r.roomId === room.id);
            } else if (res.roomId === room.id) {
              isRoomInReservation = true;
            }
            
            if (!isRoomInReservation) return false;
            
            const resCheckIn = res._checkInNorm || normalizeToParisDate(res.checkIn);
            const resCheckOut = res._checkOutNorm || normalizeToParisDate(res.checkOut);
            return (selectedCheckIn < resCheckOut && selectedCheckOut > resCheckIn);
          });
          
          const hasTempConflict = freshTempBookings.some(temp => 
            temp.roomId === room.id && 
            temp.sessionId !== sessionId &&
            selectedCheckIn < temp.checkOut && 
            selectedCheckOut > temp.checkIn
          );
          
          if (!hasConflict && !hasTempConflict) {
            finalAvailableRoomIds.push(room.id);
            if (finalAvailableRoomIds.length >= roomCount) break;
          }
        }
      }
      
      if (finalAvailableRoomIds.length < roomCount) {
        // Chambre partie pendant que le client finalisait sa réservation.
        // Sa ligne d'abandon vient d'être supprimée (le code considérait la réservation
        // faite dès la carte acceptée) et bookingStartedRef est retombé à false : sans cette
        // réécriture, ce client n'apparaîtrait dans AUCUNE tuile d'abandon.
        try {
          await setDoc(doc(db, 'lost_reservations', sessionId), {
            trackingId: bookingTrackingId,
            sessionId: sessionId,
            clientIP: userIP || null,
            roomTypeId: selectedRoomType,
            checkIn: Timestamp.fromDate(selectedCheckIn),
            checkOut: Timestamp.fromDate(selectedCheckOut),
            abandonStage: 'payment_abandoned',
            roomLostAtPayment: true,
            language: language || null,
            guestInfo: {
              name: guestInfo.name || '',
              firstName: guestInfo.firstName || '',
              email: guestInfo.email || '',
              phone: guestInfo.phone || ''
            },
            createdAt: Timestamp.now(),
            abandonedAt: Timestamp.now()
          });
        } catch (err) {
          console.error('Erreur tracking chambre perdue au paiement:', err);
        }
        alert(t.roomsNoLongerAvailable);
        
        for (const id of temporaryBookingIds) {
          await deleteTemporaryBooking(id);
        }
        
        setSubmitting(false);
        setStep(2);
        return;
      }
      


// 🔵 MODE ADMIN : Construction du roomsArray depuis selectedRoomIds directement
      const roomsArray = [];
      let roomTotal = 0;
      const nights = nightsBetween(selectedCheckOut, selectedCheckIn);

      devLog('🔍 DEBUG MODE ADMIN:');
      devLog('  selectedRoomIds:', selectedRoomIds);
      devLog('  finalAvailableRoomIds:', finalAvailableRoomIds);
      devLog('  selectedOccupancy:', selectedOccupancy);

      // En mode admin, on utilise selectedRoomIds qui contient les chambres sélectionnées directement
      for (const roomId of selectedRoomIds) {
        const room = rooms.find(r => r.id === roomId);
        if (!room) {
          console.error(`❌ Chambre ${roomId} non trouvée`);
          continue;
        }

        // ✅ MODIFIÉ : Récupérer le roomTypeId stocké lors de la sélection
        const roomTypeId = selectedOccupancy[roomId + '_type'];
        const rt = roomTypes.find(t => t.id === roomTypeId);
        if (!rt) {
          console.error(`❌ Type de chambre non trouvé pour ${room.roomNumber}`);
          continue;
        }

        // Occupancy : utiliser selectedOccupancy[roomId]
        const roomOccupancy = selectedOccupancy[roomId] || 2;
        const priceTTC = displayPrices[rt.id] || rt.basePrice || 0;
        
        // Récupérer la TVA et calculer le HT
        const vatRate = rt.vatRate ?? 0;
        const priceHT = priceTTC / (1 + vatRate / 100);
        const vatAmount = priceTTC - priceHT;

        roomsArray.push({
          roomId: roomId,
          roomNumber: room.roomNumber || room.number || '',
          roomType: rt.nameFr || rt.name || '',
          roomTypeId: roomTypeId,
          occupancy: roomOccupancy,
          pricePerNight: priceTTC,       // TTC (compatibilité)
          vatRate: vatRate,               // ✅ AJOUTÉ
          priceHT: priceHT,               // ✅ AJOUTÉ
          priceTTC: priceTTC,             // ✅ AJOUTÉ (explicite)
          vatAmount: vatAmount            // ✅ AJOUTÉ
        });

        // Calculer le total
        roomTotal += nights * priceTTC;
      }

      const optionsTotal = calculateOptionsTotal();
      
      devLog(`💰 Total chambres (admin): ${roomTotal}€, Options: ${optionsTotal}€`);
      devLog(`✅ ${roomsArray.length} chambres dans la réservation (admin):`, 
        roomsArray.map(r => `${r.roomNumber} (${r.roomType}, ${r.occupancy} pers, ${r.pricePerNight}€/nuit)`).join(', '));

      if (roomsArray.length === 0) {
        console.error('❌ ERREUR: Aucune chambre dans roomsArray !');
        alert(t.errorOccurred);
        setSubmitting(false);
        return;
      }
      
      const reservationNumber = await generateUniqueReservationNumber();

      
// ✅ DEBUG : Vérifier guestInfo (AJOUTER)
devLog('🔍 guestInfo avant findOrCreateClient (admin):', guestInfo);

// ✅ MODIFIER : Passer les données directement sans emballage
const clientResult = selectedClientId ? { id: selectedClientId } : await findOrCreateClient({
  email: guestInfo.email,
  name: guestInfo.name,
  firstName: guestInfo.firstName,
  phone: guestInfo.phone,
  company: guestInfo.company || '',
  address: guestInfo.address || '',
  city: guestInfo.city || '',
  postalCode: guestInfo.postalCode || '',
  country: guestInfo.country || 'France'
});

const clientId = clientResult.id;
devLog('✅ Client créé/trouvé (admin):', clientId);  // ✅ AJOUTER


      const formattedCustomAnswers = await (async () => {
        const answers = [];
        for (const [questionId, answer] of Object.entries(customAnswers)) {
          if (answer && answer.trim()) {
            const questionDoc = await getDoc(doc(db, 'custom_questions', questionId));
            if (questionDoc.exists()) {
              const questionData = questionDoc.data();
              const questionText = language === 'fr' ? 
                questionData.questionFr : 
                (questionData.questionEn || questionData.questionFr);
              
              answers.push({
                questionId,
                question: questionText,
                answer: answer
              });
            }
          }
        }
        return answers;
      })();



      
const reservation = {
        number: reservationNumber,
        rooms: roomsArray,
        checkIn: Timestamp.fromDate(selectedCheckIn),
        checkOut: Timestamp.fromDate(selectedCheckOut),
        language: language,
        clientId: clientId,  // ✅ Utiliser le clientId créé/trouvé
couponApplied: appliedCoupon ? {
          code: appliedCoupon.code,
          description: appliedCoupon.description,
          reductionType: appliedCoupon.reductionType,
          reductionAmount: appliedCoupon.reductionAmount,
          discountCalculated: calculateCouponDiscount(roomTotal),
          appliedAt: Timestamp.now()
        } : null,
        client: {
          name: guestInfo.name,
          firstName: guestInfo.firstName,
          company: guestInfo.company,
          email: guestInfo.email,
          phone: guestInfo.phone,
          address: guestInfo.address,
          city: guestInfo.city,
          postalCode: guestInfo.postalCode,
          country: guestInfo.country
        },
        guests: {
          total: nombreDePersonnes(roomsArray),
          adults: nombreDePersonnes(roomsArray),
          children: 0
        },
        pricing: {
          total: roomTotal + optionsTotal,
          roomTotal: roomTotal,
          optionsTotal: optionsTotal,
          paid: 0,
          roomDetails: roomsArray.map(room => ({
            roomId: room.roomId,
            pricePerNight: room.pricePerNight,
            totalPrice: room.pricePerNight * nightsBetween(selectedCheckOut, selectedCheckIn)
          }))
        },
        options: [],
        consumptions: selectedOptions.map(optionId => {
          const option = loadedOptions.find(o => o.id === optionId);
          const nights = nightsBetween(selectedCheckOut, selectedCheckIn);
          const totalGuests = getTotalGuests();
          const vatRate = option.vatRate || 0;
          const priceIncludesVat = option.priceIncludesVat !== false;
          
          let quantity = 1;
          let totalPrice = option.price;
          
          switch(option.unit) {
            case 'personne/jour':
              quantity = totalGuests * nights;
              totalPrice = option.price * quantity;
              break;
            case 'personne':
              quantity = totalGuests;
              totalPrice = option.price * quantity;
              break;
            case 'jour':
              quantity = nights;
              totalPrice = option.price * quantity;
              break;
            case 'chambre':
              quantity = roomCount;
              totalPrice = option.price * quantity;
              break;
            case 'chambre/jour':
              quantity = roomCount * nights;
              totalPrice = option.price * quantity;
              break;
            case 'réservation':
            case 'unité':
            default:
              quantity = 1;
              totalPrice = option.price;
              break;
          }
          
          const totalHT = priceIncludesVat ? 
            (totalPrice / (1 + vatRate / 100)) : 
            totalPrice;
          const totalTTC = priceIncludesVat ? 
            totalPrice : 
            (totalPrice * (1 + vatRate / 100));
          const totalVAT = totalTTC - totalHT;
          
          const description = language === 'fr' ? 
            (option.nameFr || option.name) : 
            option.name;
          
          return {
            id: crypto.randomUUID(),
            type: option.category || 'other',
            isOption: true,
            optionId: option.id,
            description: description,
            quantity: quantity,
            unitPrice: option.price,
            totalPrice: totalPrice,
            unit: option.unit,
date: selectedCheckIn.toISOString(),
            status: 'pending',
            vatRate: vatRate,
            priceIncludesVat: priceIncludesVat,
            unitPriceHT: Math.round((priceIncludesVat ? option.price / (1 + vatRate / 100) : option.price) * 100) / 100,
            unitPriceTTC: Math.round((priceIncludesVat ? option.price : option.price * (1 + vatRate / 100)) * 100) / 100,
            totalHT: Math.round(totalHT * 100) / 100,
            totalTTC: Math.round(totalTTC * 100) / 100,
            totalVAT: Math.round(totalVAT * 100) / 100
          };
        }),
        status: 'pending',
        payment: {
          method: 'admin',
          status: 'pending',
          notes: 'Réservation créée manuellement par un administrateur'
        },
        source: 'admin',
        notes: guestInfo.message,
        customAnswers: formattedCustomAnswers,
        guestsDetails: guestsDetails.length > 0 ? guestsDetails : undefined,
        guestsCompleted: guestsDetails.length > 1 && guestsDetails.every((g, i) => i === 0 || (g.firstName && g.lastName)),
        guestsCompletedAt: (guestsDetails.length > 1 && guestsDetails.every((g, i) => i === 0 || (g.firstName && g.lastName))) ? Timestamp.now() : null,
        guestsCompletedBy: (guestsDetails.length > 1 && guestsDetails.every((g, i) => i === 0 || (g.firstName && g.lastName))) ? 'admin' : null,
        createdAt: Timestamp.now(),
        createdBy: userData?.name || userData?.email || 'Admin'
      };
      
const reservationId = await createReservation(reservation);

// ✅ NOTIFICATION HEADER ADMIN
try {
  await addDoc(collection(db, 'adminNotifications'), {
    type: 'new_reservation',
    reservationId: reservationId,
    reservationNumber: reservationNumber,
    clientName: `${guestInfo.firstName} ${guestInfo.name}`,
    checkIn: format(selectedCheckIn, 'dd/MM/yyyy'),
    checkOut: format(selectedCheckOut, 'dd/MM/yyyy'),
    createdAt: Timestamp.now(),
    read: false
  });
} catch (error) {
  console.error('Erreur notification admin:', error);
}

// 📧 ENVOYER LES EMAILS DE DEMANDE DE RÉSERVATION
try {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://valetier.maisonceronne.com';
  
  // Récupérer les templates configurés pour immediate_booking_request et immediate_booking_request_admin
  const templatesSnapshot = await getDocs(
    query(
      collection(db, 'email_templates'),
      where('automation', 'in', ['immediate_booking_request', 'immediate_booking_request_admin'])
    )
  );
  
  if (!templatesSnapshot.empty) {
    devLog(`📧 Envoi de ${templatesSnapshot.size} email(s) automatique(s)...`);
    
    for (const templateDoc of templatesSnapshot.docs) {
      try {
        const response = await fetch(`${backendUrl}/api/email/send-reservation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            reservationId: reservationId,
            templateId: templateDoc.id,
            language: language || 'fr',
            sentBy: adminMode ? (userData?.email || 'Admin') : 'SYSTEM'
          })
        });
        
        const result = await response.json();
        if (result.success) {
          devLog(`✅ Email ${templateDoc.data().name} envoyé`);
        }
      } catch (emailError) {
        console.error(`Erreur envoi email ${templateDoc.id}:`, emailError);
      }
    }
  }
} catch (error) {
  console.error('Erreur envoi emails automatiques:', error);
  // Ne pas bloquer la réservation si l'envoi échoue
}

// ✅ NOTIFICATION RÉCAP QUOTIDIEN
try {
  await addDailyNotification({
    type: 'new',
    category: 'room',
    data: {
      reservationNumber: reservationNumber,
      reservationId: reservationId,
      clientName: `${guestInfo.firstName} ${guestInfo.name}`,
      email: guestInfo.email,
      checkIn: format(selectedCheckIn, 'dd/MM/yyyy'),
      checkOut: format(selectedCheckOut, 'dd/MM/yyyy'),
      roomNumber: roomsArray[0]?.roomNumber || '',
      roomType: roomsArray[0]?.roomType || '',
      price: roomTotal + optionsTotal
    }
  });
} catch (error) {
  console.error('Erreur notification quotidienne:', error);
}
   
// ✅ MODIFIER : Utiliser clientId
await updateClientReservationStats(clientId, reservationNumber, 'pending');

// Recalcul compteur de visites (totalReservations / isReturningClient)
try {
  await recalculateClientStats(clientId);
  console.log('✅ Stats client recalculées après création résa admin (light)');
} catch (statsError) {
  console.warn('⚠️ Erreur recalcul stats client (non bloquant):', statsError);
}

// ✅ Mettre à jour les accompagnants fréquents du client
if (guestsDetails.length > 1) {
  await updateFrequentCompanions(clientId, guestsDetails, reservationId);
}

      // Marquer le coupon comme utilisé
if (appliedCoupon) {
        try {
          await updateDoc(doc(db, 'coupons', appliedCoupon.id), {
            isUsed: true,
            usedAt: Timestamp.now(),
            usedByReservation: reservationId
          });

        // LA NOTE FISCALE DU BON. Sans elle, la nuit payée avec un bon cadeau
        // n'était déclarée NULLE PART : la vente du bon n'est pas taxable (bon
        // à usages multiples, art. 256 ter CGI), et son usage ne produisait
        // rien. Le serveur ne note que les bons ACHETÉS — un code issu d'une
        // annulation a déjà sa note, faite quand l'argent est rentré.
        // ⚠️ Cette page existe en DEUX exemplaires : ici et dans VALETIER.
        //    Toute correction doit être reportée sur l'autre.
        try {
          await fetch(`${process.env.REACT_APP_API_URL}/notes-caisse/bon-utilise`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ reservationId, couponCode: appliedCoupon.code }),
          });
        } catch (e) {
          // Une note manquée ne doit jamais faire échouer la réservation.
          console.error('Note du bon cadeau :', e);
        }
          devLog('✅ Coupon marqué comme utilisé');
          
          // Logger l'activité
          await logCouponApplied(
            reservationId,
            'admin-booking',
            guestInfo.name || 'Client',
            {
              code: appliedCoupon.code,
              discount: `${appliedCoupon.reductionType === 'percentage' ? appliedCoupon.reductionAmount + '%' : 
                         appliedCoupon.reductionType === 'fixedPerProduct' ? appliedCoupon.reductionAmount + '€/nuit' :
                         appliedCoupon.reductionAmount + '€'}`
            }
          );
          devLog('✅ Log coupon créé');
        } catch (error) {
          console.error('Erreur marquage coupon:', error);
        }
      }
      
      devLog('✅ Suppression des temporary bookings...');
      for (const id of temporaryBookingIds) {
        await deleteTemporaryBooking(id);
      }
      
      navigate(`/admin/reservations/${reservationId}`);
      
    } catch (error) {
      console.error('Erreur création réservation:', error);
      alert(t.errorOccurred);
    } finally {
      setSubmitting(false);
    }
  };










  // Écran de chargement
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

return (
    <div className="min-h-screen bg-white">
      {splitBlockedMessage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSplitBlockedMessage(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-gray-900 whitespace-pre-line leading-relaxed">
              {splitBlockedMessage[language] || splitBlockedMessage.fr}
            </p>
            <button
              onClick={() => setSplitBlockedMessage(null)}
              className="mt-5 w-full bg-black text-white py-2.5 rounded-lg font-medium hover:bg-gray-800"
            >
              {language === 'en' ? 'Close' : 'Fermer'}
            </button>
          </div>
        </div>
      )}
      <header className={`bg-black text-white text-center relative ${
        adminMode ? 'p-4' : 'p-8 pt-14 sm:pt-8'
      }`}>
        {adminMode && (
          <div className="absolute top-2 left-4 bg-yellow-400 text-black px-3 py-1 text-xs font-bold uppercase rounded">
            Mode Admininstrateur
          </div>
        )}
        {!adminMode && (
          <button
            onClick={toggleLanguage}
            className="absolute top-4 right-4 px-3 py-1.5 bg-white text-black rounded hover:bg-gray-200 flex items-center gap-2 text-sm"
          >
            <Globe size={16} />
            {language === 'fr' ? 'EN' : 'FR'}
          </button>
        )}
        {/* Logo SVG Maison Ceronne */}
        <div className={`mx-auto ${adminMode ? 'w-32' : 'w-80 sm:w-96'}`}>
          <svg viewBox="0 0 191.75 18.54" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <g fill="#fff">
              <path d="M17.95,18.25l4.09-.04c.4-.89.8-1.77,1.19-2.66h.02s5.96.01,5.96.01c.39.88.78,1.77,1.17,2.65h4.01S26.21.43,26.21.43l-8.26,17.82ZM27.78,12.47h-3.09l1.55-3.64,1.54,3.64Z"/>
              <rect x="35.9" y=".72" width="3.51" height="17.5"/>
              <path d="M51.66,13c0-.99-.25-1.8-.74-2.43-.84-1.07-2.05-1.91-3.4-2.3-1.25-.37-1.13-.24-2.17-.91-.51-.33-.76-.8-.76-1.43,0-.97.8-1.77,1.77-1.77s1.76.8,1.76,1.76h3.53c0-1.45-.52-2.7-1.56-3.74-1.04-1.04-2.29-1.56-3.74-1.56s-2.71.52-3.76,1.56c-1.04,1.04-1.56,2.29-1.56,3.74,0,.97.24,1.77.72,2.42.8,1.1,2,1.98,3.29,2.42,1.23.42.92.31,2.18.78.6.22.89.69.89,1.44,0,.97-.8,1.77-1.77,1.77v.02c-.97,0-1.76-.79-1.76-1.77h-3.54c0,1.47.52,2.71,1.56,3.76,1.04,1.04,2.29,1.56,3.76,1.56s2.7-.52,3.74-1.56c1.04-1.04,1.56-2.29,1.56-3.76Z"/>
              <path d="M55.43,15.73c1.73,1.73,3.81,2.59,6.24,2.59s4.52-.87,6.25-2.59c1.74-1.73,2.6-3.81,2.6-6.24s-.87-4.52-2.6-6.25c-1.74-1.74-3.82-2.6-6.25-2.6s-4.52.87-6.24,2.6c-1.73,1.74-2.59,3.82-2.59,6.25s.87,4.52,2.59,6.24ZM57.92,5.72c1.03-1.04,2.28-1.56,3.74-1.56s2.7.52,3.74,1.56c1.04,1.04,1.56,2.29,1.56,3.74s-.52,2.71-1.56,3.74c-1.04,1.03-2.29,1.55-3.74,1.55s-2.71-.52-3.74-1.55c-1.03-1.03-1.55-2.28-1.55-3.74s.52-2.7,1.55-3.74Z"/>
              <polygon points="75.31 8.54 85.81 18.54 85.81 .72 82.3 .72 82.3 10.39 71.81 .4 71.81 18.22 75.31 18.22 75.31 8.54"/>
              <polygon points="108.35 17.18 111.72 17.18 118.46 17.18 118.46 13.8 111.72 13.8 111.72 10.43 116.78 10.43 116.78 7.06 111.72 7.06 111.72 3.68 118.46 3.68 118.46 .31 108.35 .31 108.35 17.18"/>
              <path d="M130.45,9.59c.71-1,1.07-1.93,1.07-3.18v-.52c0-1.62-.47-2.71-1.63-3.87-1.16-1.16-2.76-1.73-4.38-1.73h-5.48v16.89h3.39v-6.59l4.31,6.59h4.12l-3.81-5.87c1.11-.42,1.71-.71,2.42-1.72ZM127.37,7.75c-.49.49-.85.67-1.55.67h-2.42V3.66h1.89c.7,0,1.58.18,2.07.68s.67.96.67,1.65-.18,1.27-.67,1.76Z"/>
              <path d="M141.51.23c-2.34,0-4.35.84-6.02,2.51-1.66,1.67-2.5,3.68-2.5,6.03s.84,4.35,2.5,6.02c1.66,1.66,3.67,2.5,6.02,2.5s4.35-.84,6.03-2.5c1.67-1.66,2.51-3.67,2.51-6.02s-.84-4.35-2.51-6.03c-1.67-1.67-3.68-2.51-6.03-2.51ZM145.12,12.36c-1,.99-2.21,1.5-3.61,1.5s-2.62-.5-3.61-1.5c-.99-.99-1.5-2.2-1.5-3.61s.5-2.61,1.5-3.61c.99-1,2.2-1.51,3.61-1.51s2.61.5,3.61,1.51c1,1,1.51,2.21,1.51,3.61s-.5,2.62-1.51,3.61Z"/>
              <polygon points="161.4 9.64 151.29 0 151.29 17.18 154.67 17.18 154.67 7.85 164.78 17.49 164.78 .31 161.4 .31 161.4 9.64"/>
              <polygon points="176.58 9.64 166.46 0 166.46 17.18 169.84 17.18 169.84 7.85 179.96 17.49 179.96 .31 176.58 .31 176.58 9.64"/>
              <polygon points="191.75 3.68 191.75 .31 181.63 .31 181.63 17.18 185.01 17.18 191.75 17.18 191.75 13.8 185.01 13.8 185.01 10.43 190.06 10.43 190.06 7.06 185.01 7.06 185.01 3.68 191.75 3.68"/>
              <path d="M100.81.21c1.2,0,2.33.23,3.39.69,1.06.46,2,1.08,2.8,1.87l-2.47,2.47c-1.04-1.03-2.28-1.55-3.7-1.55s-2.68.52-3.7,1.55c-1.02,1.03-1.53,2.26-1.53,3.7s.52,2.68,1.53,3.7c1.02,1.02,2.25,1.53,3.7,1.53s2.68-.52,3.71-1.55l2.47,2.47c-.8.79-1.74,1.42-2.8,1.88s-2.19.69-3.39.69c-2.4,0-4.46-.86-6.17-2.56-1.71-1.71-2.56-3.77-2.56-6.17s.86-4.46,2.56-6.18c1.71-1.72,3.77-2.58,6.17-2.58v.04Z"/>
              <path d="M8.43,12.22L16.87.65v17.62h-3.47v-7.14l-4.97,6.86-4.97-6.86v7.14H0V.65l8.43,11.57Z"/>
            </g>
          </svg>
        </div>
        {!adminMode && (
          <div className="mt-4">
            <h1 className="text-sm uppercase tracking-wider opacity-80">
              {language === 'fr' ? 'Réserver une chambre' : 'Book a room'}
            </h1>
            <p className="text-xs opacity-60 mt-2 max-w-md mx-auto normal-case tracking-normal">
              {language === 'fr'
                ? "Réservation en ligne d'une chambre à Maison Ceronne, entre boutique-hôtel et maison d'hôtes dans le Perche."
                : 'Online booking of a room at Maison Ceronne, between a boutique hotel and a guesthouse in Le Perche.'}
            </p>
          </div>
        )}
      </header>

      {/* Bloc de texte personnalisable */}
{!adminMode && step === 1 && (bookingLightTextFr || bookingLightTextEn) && (
          <div className="max-w-6xl mx-auto px-2 sm:px-8 py-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-wrap">
              {language === 'fr' ? bookingLightTextFr : bookingLightTextEn}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-2 sm:px-8 py-8">
        <StepIndicator currentStep={step} language={language} t={t} onStepClick={handleStepClick} />

        {step === 1 && (
          <>
            <div className="bg-white border border-gray-200 p-8">
              {/* Message personnalisé */}
              {calendarMessage[language] && (
                <p className="font-semibold text-base text-center text-green-600 mb-6 tracking-wider uppercase">
                  {calendarMessage[language]}
                </p>
              )}

              <BookingCalendar
                currentMonth={currentMonth}
                setCurrentMonth={setCurrentMonth}
                selectedCheckIn={selectedCheckIn}
                selectedCheckOut={selectedCheckOut}
                hoveredDate={hoveredDate}
                invalidDateMessage={invalidDateMessage}
                monthDayStatuses={monthDayStatuses}
                monthMinPrices={monthMinPrices}
                reservations={reservations}
                handleDateClick={handleDateClick}
                setHoveredDate={setHoveredDate}
                getPossibleDurationsFromDate={getPossibleDurationsFromDateWrapper}
                language={language}
                t={t}
                dateLocale={dateLocale}
                loadingMonthPrices={loadingMonthPrices}
              />

            </div>
          </>
        )}

        {/* Bandeau fixe bas — guidage sélection dates */}
        {step === 1 && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-black text-white shadow-2xl">
            <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <CalendarDays className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                {!selectedCheckIn && (
                  <p className="text-base sm:text-lg font-bold tracking-wide uppercase animate-pulse">{t.selectArrivalDate}</p>
                )}
                {selectedCheckIn && !selectedCheckOut && (
                  <p className="text-base sm:text-lg font-bold tracking-wide uppercase animate-pulse">{t.selectDepartureDate}</p>
                )}
                {selectedCheckIn && selectedCheckOut && (
                  <div>
                    <p className="text-base sm:text-lg font-bold uppercase">
                      {format(selectedCheckIn, language === 'fr' ? 'dd MMM' : 'MMM dd', { locale: dateLocale })} → {format(selectedCheckOut, language === 'fr' ? 'dd MMM yyyy' : 'MMM dd, yyyy', { locale: dateLocale })}
                      <span className="ml-2 text-gray-400 font-light">
                        · {nightsBetween(selectedCheckOut, selectedCheckIn)} {nightsBetween(selectedCheckOut, selectedCheckIn) > 1 ? t.nights : t.night}
                      </span>
                    </p>
                    {!isValidBookingPatternWrapper(selectedCheckIn, selectedCheckOut) && (
                      <p className="text-red-400 text-sm mt-0.5">{t.durationNotAvailable}</p>
                    )}
                  </div>
                )}
              </div>

              {selectedCheckIn && selectedCheckOut && isValidBookingPatternWrapper(selectedCheckIn, selectedCheckOut) && (
                <button
                  onClick={handleValidateDates}
                  disabled={transitioning}
                  className="w-full sm:w-auto px-10 py-4 bg-white text-black font-semibold hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg flex items-center justify-center gap-2 flex-shrink-0 transition-colors text-base"
                >
                  <Check size={20} />
                  {transitioning ? (language === 'fr' ? 'Chargement...' : 'Loading...') : t.validateDates}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Bandeau fixe bas — étape 2 sélection chambre */}
        {step === 2 && !splitStayInfo?.showingIntro && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-black text-white shadow-2xl">
            <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-3">
                <BedDouble className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                {getTotalRooms() === 0 && (
                  <p className="text-base sm:text-lg font-bold tracking-wide uppercase animate-pulse">{t.selectRoomTypeBanner}</p>
                )}
                {getTotalRooms() > 0 && (
                  <p className="text-base sm:text-lg font-bold tracking-wide uppercase">
                    {(t.roomsSelected || '{n} chambre{s} sélectionnée{s}')
                      .replace('{n}', getTotalRooms())
                      .replace(/{s}/g, getTotalRooms() > 1 ? 's' : '')}
                  </p>
                )}
              </div>

              {getTotalRooms() > 0 && !(!adminMode && getTotalRooms() > (defaultRules.maxRoomsPerBooking || 5)) && (
                <button
                  onClick={handleContinueToOptions}
                  disabled={transitioning}
                  className="w-full sm:w-auto px-10 py-4 bg-white text-black font-semibold hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg flex items-center justify-center gap-2 flex-shrink-0 transition-colors text-base"
                >
                  <Check size={20} />
                  {transitioning ? (language === 'fr' ? 'Chargement...' : 'Loading...') : t.continueToOptions}
                </button>
              )}
            </div>
          </div>
        )}

        {step === 2 && splitStayInfo?.showingIntro && (
          <div className="bg-white border border-gray-200 p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-xl sm:text-2xl font-light">
                {splitStayInfo.currentSegment === 0 ? t.splitStayTitle : t.splitStayTitlePart2}
              </h2>
              <p className="text-sm text-gray-600 mt-2">
                {(splitStayInfo.currentSegment === 0 ? t.splitStayIntro : t.splitStayIntro)
                  .replace('{from}', format(splitStayInfo.originalCheckIn, language === 'fr' ? 'dd MMMM' : 'MMMM dd', { locale: dateLocale }))
                  .replace('{to}', format(splitStayInfo.originalCheckOut, language === 'fr' ? 'dd MMMM yyyy' : 'MMMM dd, yyyy', { locale: dateLocale }))}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {splitStayInfo.segments.map((seg, i) => {
                const isDone = i < splitStayInfo.currentSegment;
                const isCurrent = i === splitStayInfo.currentSegment;
                const segRoomTypes = seg.roomTypes || (seg.roomType ? [seg.roomType] : []);
                const roomName = segRoomTypes.length === 1
                  ? (language === 'fr' ? (segRoomTypes[0].nameFr || segRoomTypes[0].name) : (segRoomTypes[0].nameEn || segRoomTypes[0].name))
                  : (language === 'fr' ? 'Plusieurs types disponibles' : 'Multiple room types available');
                const segNights = nightsBetween(seg.checkOut, seg.checkIn);
                const minPrice = segRoomTypes.length > 0
                  ? Math.min(...segRoomTypes.map(rt => (displayPrices[rt.id] || rt.basePrice || 0) * segNights))
                  : null;
                return (
                  <div key={i} className={`flex items-start gap-3 p-4 rounded-lg border ${isCurrent ? 'border-black bg-gray-50' : isDone ? 'border-gray-300 bg-gray-50 opacity-70' : 'border-gray-200'}`}>
                    <div className={`mt-0.5 w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold ${isDone ? 'bg-black text-white' : isCurrent ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'}`}>
                      {isDone ? '✓' : i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm">{roomName}</p>
                      <p className="text-sm text-gray-600">
                        {format(seg.checkIn, language === 'fr' ? 'dd MMM' : 'MMM dd', { locale: dateLocale })}
                        {' → '}
                        {format(seg.checkOut, language === 'fr' ? 'dd MMM yyyy' : 'MMM dd, yyyy', { locale: dateLocale })}
                        {' '}({segNights} {segNights > 1 ? t.nights : t.night})
                        {minPrice > 0 ? ` · ${language === 'fr' ? 'à partir de' : 'from'} ${minPrice}€ (${Math.round(minPrice / segNights)}€ ${language === 'fr' ? 'la nuit' : 'per night'})` : ''}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="text-sm text-gray-500 mb-6">
              {splitStayInfo.currentSegment === 0 ? t.splitStayInstructions : t.splitStayInstructionsPart2}
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={async () => {
                  const seg = splitStayInfo.segments[splitStayInfo.currentSegment];
                  setSelectedCheckIn(seg.checkIn);
                  setSelectedCheckOut(seg.checkOut);
                  await proceedToRoomSelection(seg.checkIn, seg.checkOut, nightsBetween(splitStayInfo.originalCheckOut, splitStayInfo.originalCheckIn), true);
                  setSplitStayInfo(prev => ({ ...prev, showingIntro: false }));
                }}
                className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 font-medium text-sm sm:text-base"
              >
                {t.splitStayBookSegment
                  .replace('{from}', format(splitStayInfo.segments[splitStayInfo.currentSegment].checkIn, language === 'fr' ? 'dd MMM' : 'MMM dd', { locale: dateLocale }))
                  .replace('{to}', format(splitStayInfo.segments[splitStayInfo.currentSegment].checkOut, language === 'fr' ? 'dd MMM yyyy' : 'MMM dd, yyyy', { locale: dateLocale }))}
              </button>
              <button
                onClick={() => { setSplitStayInfo(null); setStep(1); }}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm sm:text-base"
              >
                {t.chooseOtherDates}
              </button>
            </div>
          </div>
        )}

        {step === 2 && !splitStayInfo?.showingIntro && (
          <div className="bg-white border border-gray-200 p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-light">{t.selectRooms}</h2>
              <p className="text-sm text-gray-600 mt-2">
                {language === 'fr' ? 'Du' : 'From'} <strong>{format(selectedCheckIn, language === 'fr' ? 'dd MMMM yyyy' : 'MMMM dd, yyyy', { locale: dateLocale })}</strong> {language === 'fr' ? 'au' : 'to'}{' '}
                <strong>{format(selectedCheckOut, language === 'fr' ? 'dd MMMM yyyy' : 'MMMM dd, yyyy', { locale: dateLocale })}</strong>
                {' '}({nightsBetween(selectedCheckOut, selectedCheckIn)} {nightsBetween(selectedCheckOut, selectedCheckIn) > 1 ? t.nights : t.night})
              </p>
            </div>

            <RoomSelectionStep
              adminMode={adminMode}
              language={language}
              t={t}
              dateLocale={dateLocale}
              roomTypes={roomTypes}
              rooms={rooms}
              reservations={reservations}
              temporaryBookings={temporaryBookings}
              roomBlocks={roomBlocks}
              selectedCheckIn={selectedCheckIn}
              selectedCheckOut={selectedCheckOut}
              selectedRoomType={selectedRoomType}
              setSelectedRoomType={setSelectedRoomType}
              selectedRoomCount={selectedRoomCount}
              setSelectedRoomCount={setSelectedRoomCount}
              selectedOccupancy={selectedOccupancy}
              setSelectedOccupancy={setSelectedOccupancy}
              selectedRoomOccupancies={selectedRoomOccupancies}
              setSelectedRoomOccupancies={setSelectedRoomOccupancies}
              selectedRoomIds={selectedRoomIds}
              setSelectedRoomIds={setSelectedRoomIds}
              pendingRoomSelections={pendingRoomSelections}
              setPendingRoomSelections={setPendingRoomSelections}
              selectedImageIndex={selectedImageIndex}
              setSelectedImageIndex={setSelectedImageIndex}
              displayPrices={displayPrices}
              defaultRules={defaultRules}
              sessionId={sessionId}
              getMaxAvailableRoomsForPeriod={getMaxAvailableRoomsForPeriod}
              getMaxAvailableOccupancyWrapper={getMaxAvailableOccupancyWrapper}
              isRoomTypeAvailableForDatesWrapper={isRoomTypeAvailableForDatesWrapper}
              getRoomPriceForMultipleRooms={getRoomPriceForMultipleRooms}
              longStayDiscounts={longStayDiscounts}
              getTotalGuests={getTotalGuests}
              getTotalRooms={getTotalRooms}
              getTotalGuestsForType={getTotalGuestsForType}
              pluralize={pluralize}
              setStep={setStep}
              handleContinueToOptions={handleContinueToOptions}
              transitioning={transitioning}
              isSplitStaySegment={!!(splitStayInfo && !splitStayInfo.showingIntro)}
              splitStaySegmentLabel={splitStayInfo && !splitStayInfo.showingIntro && splitStayInfo.currentSegment < (splitStayInfo.segments?.length ?? 1) - 1 ? (language === 'fr' ? `Valider le segment ${splitStayInfo.currentSegment + 1} et choisir la suite →` : `Confirm segment ${splitStayInfo.currentSegment + 1} and continue →`) : undefined}
              createTemporaryBooking={createTemporaryBooking}
              setTemporaryBookingIds={setTemporaryBookingIds}
            />
          </div>
        )}

        {step === 3 && (
          <div className="bg-white border border-gray-200 p-8">
            <OptionsSelection
              selectedOptions={selectedOptions}
              setSelectedOptions={setSelectedOptions}
              selectedCheckIn={selectedCheckIn}
              selectedCheckOut={selectedCheckOut}
              selectedOccupancy={selectedOccupancy}
              selectedRoomType={selectedRoomType}
              calculateOptionsTotal={calculateOptionsTotal}
              onOptionsLoaded={setLoadedOptions}
              language={language}
              t={t}
            />

            <div className="mt-8 flex justify-between">
              <button 
                onClick={handleBackToRooms}
                className="px-6 py-3 border border-gray-300 hover:bg-gray-50 rounded-lg"
              >
                {t.backToRooms}
              </button>
              <button 
                onClick={() => setStep(4)}
                className="px-6 py-3 bg-black text-white hover:bg-gray-800 rounded-lg"
              >
                {t.continue}
              </button>
            </div>
          </div>
        )}

{step === 4 && (
          <div className="bg-white border border-gray-200 p-8">

<GuestInfoForm
  guestInfo={guestInfo}
  setGuestInfo={setGuestInfo}
  selectedCheckIn={selectedCheckIn}
  selectedCheckOut={selectedCheckOut}
  selectedRoomType={selectedRoomType}
  selectedOccupancy={selectedOccupancy}
  selectedRoomCount={selectedRoomCount}           // 🔵 AJOUTÉ
  selectedRoomOccupancies={selectedRoomOccupancies} // 🔵 AJOUTÉ
  selectedRoomIds={selectedRoomIds}               // ✅ NOUVEAU
  rooms={rooms}                                   // ✅ NOUVEAU
  selectedOptions={selectedOptions}
  availableOptions={loadedOptions}
  roomTypes={roomTypes}
  displayPrices={displayPrices}                   // ✅ AJOUTÉ POUR CORRIGER LE PRIX
  calculateTotal={calculateTotal}
  calculateOptionsTotal={calculateOptionsTotal}
  differenceInDays={differenceInDays}
  format={format}
  fr={fr}
  language={language}
  t={t}
  dateLocale={dateLocale}
  customAnswers={customAnswers}
  setCustomAnswers={setCustomAnswers}
  adminMode={adminMode}
  selectedClientId={selectedClientId}
  setSelectedClientId={setSelectedClientId}
  appliedCoupon={appliedCoupon}
  calculateCouponDiscount={calculateCouponDiscount}
  guestsDetails={guestsDetails}
  setGuestsDetails={setGuestsDetails}
  onIdentityFieldBlur={handleIdentityFieldBlur}
/>



{/* Bloc code de réservation */}
            <div className="mt-6 border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">
                {language === 'fr' ? 'Code de réservation' : 'Reservation code'}
              </h3>
              


{!appliedCoupon ? (
                <div className="space-y-3">
                  {adminMode && availableCoupons.length > 0 ? (
                    /* MODE ADMIN : Select uniquement */
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        Sélectionner un coupon
                      </label>
                      <select
                        value={couponCode}
                        onChange={async (e) => {
                          const code = e.target.value;
                          setCouponCode(code);
                          if (code) {
                            setCouponError('');
                            // Valider automatiquement avec le code sélectionné
                            await validateCouponCode(code);
                          } else {
                            setCouponError('');
                            setAppliedCoupon(null);
                          }
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={validatingCoupon}
                      >
                        <option value="">-- Choisir un coupon (optionnel) --</option>
                        {availableCoupons.map(coupon => (
                          <option key={coupon.id} value={coupon.code}>
                            {coupon.code} - {coupon.description} ({coupon.reductionType === 'percentage' ? `${coupon.reductionAmount}%` : `${coupon.reductionAmount}€`})
                          </option>
                        ))}
                      </select>
                      {validatingCoupon && (
                        <p className="text-xs text-gray-500 mt-1">Validation en cours...</p>
                      )}
                    </div>
                  ) : (
                    /* MODE PUBLIC : Input texte + bouton */
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="text"
                        value={couponCode}
                        onChange={(e) => {
                          setCouponCode(e.target.value.toLowerCase());
                          setCouponError('');
                        }}
                        placeholder={language === 'fr' ? 'Entrez votre code' : 'Enter your code'}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        maxLength={6}
                        disabled={validatingCoupon}
                      />
                      <button
                        onClick={() => validateCouponCode()}
                        disabled={validatingCoupon || !couponCode.trim()}
                        className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
                      >
                        {validatingCoupon 
                          ? (language === 'fr' ? 'Validation...' : 'Validating...') 
                          : (language === 'fr' ? 'Valider' : 'Validate')}
                      </button>
                    </div>
                  )}
                  {couponError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{couponError}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-green-800">
                        {language === 'fr' ? 'Code appliqué :' : 'Code applied:'} <span className="font-mono">{appliedCoupon.code}</span>
                      </p>
                      {appliedCoupon.description && (
                        <p className="text-sm text-green-700 mt-1">{appliedCoupon.description}</p>
                      )}
                      <p className="text-sm text-green-600 mt-2">
                        {language === 'fr' ? 'Réduction :' : 'Discount:'} 
                        {appliedCoupon.reductionType === 'percentage' && ` ${appliedCoupon.reductionAmount}% sur les chambres`}
                        {appliedCoupon.reductionType === 'fixedPerProduct' && ` ${appliedCoupon.reductionAmount}€ par nuit`}
                        {appliedCoupon.reductionType === 'settlementCode' && ` ${appliedCoupon.reductionAmount}€ d'avoir`}
                      </p>
                    </div>
                    <button
                      onClick={removeCoupon}
                      className="text-green-600 hover:text-green-800"
                      title={language === 'fr' ? 'Retirer le code' : 'Remove code'}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            {getTotalRooms() > 1 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm">
                  <strong>{t.note}</strong> {t.youAreBooking.replace('{rooms}', getTotalRooms()).replace(/{s}/g, getTotalRooms() > 1 ? 's' : '').replace('{guests}', getTotalGuests()).replace(/{s}/g, getTotalGuests() > 1 ? 's' : '')}
                </p>
              </div>
            )}

            {/* Bloc CGV */}
            {!adminMode && (cgvLinks[language] || cgvTexts[language]) && (
              <div className={`mt-4 p-4 rounded-lg border ${cgvError ? 'bg-red-50 border-red-300' : 'bg-gray-100 border-gray-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cgvAccepted}
                    onChange={(e) => {
                      setCgvAccepted(e.target.checked);
                      if (e.target.checked) setCgvError(false);
                    }}
                    className="mt-1 w-5 h-5 rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className="text-sm text-gray-700">
                    {t.cgvAccept}{' '}
                    {cgvUseText && cgvTexts[language] ? (
                      <button
                        
onClick={(e) => {
  e.preventDefault();
  e.stopPropagation();
  setCgvModalOpen(true);
  trackCGVViewed(language);
}}

                        className="text-black underline hover:no-underline font-medium"
                      >
                        {t.cgvLink}
                      </button>
                    ) : (
                      <a
                        href={cgvLinks[language]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-black underline hover:no-underline font-medium"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t.cgvLink}
                      </a>
                    )}
                    {cgvRequired && <span className="text-red-500 ml-1">*</span>}
                  </span>
                </label>
                {cgvError && (
                  <p className="mt-2 text-sm text-red-600 font-medium">
                    {t.cgvRequired}
                  </p>
                )}
              </div>
            )}



<div className="flex justify-between pt-4">
              <button 
                onClick={() => setStep(3)}
                className="px-6 py-3 border border-gray-300 hover:bg-gray-50 rounded-lg"
                disabled={submitting}
              >
                {t.back}
              </button>
              
              <button 
                onClick={() => {
                  // Vérifier CGV si requis (mode public uniquement)
                  if (!adminMode && cgvRequired && (cgvLinks[language] || cgvTexts[language]) && !cgvAccepted) {
                    setCgvError(true);
                    return;
                  }
                  
                  if (adminMode) {
                    handleSubmitWithoutPayment();
                  } else {
                    // Si coupon avec ignorePaymentRequirements ET total <= 0, soumettre directement
                    if (appliedCoupon?.ignorePaymentRequirements || calculateTotal() <= 0) {
                      handleSubmitWithPayment(null);
                    
} else {
  trackGuestInfoCompleted(guestInfo.email, language);
  trackStepProgress(5, language);
  trackPaymentInitiated(calculateTotal(), language);
  setStep(5);
}

                  }
                }}
                disabled={!guestInfo.name || !guestInfo.email || !guestInfo.phone || submitting}
                className="px-6 py-3 bg-black text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg"
              >
                {submitting ? (adminMode ? 'Création...' : 'Envoi...') : (
                  adminMode ? 'Créer la réservation' : (
                    appliedCoupon?.ignorePaymentRequirements || calculateTotal() <= 0
                      ? (language === 'fr' ? 'Confirmer la réservation' : 'Confirm booking')
                      : t.continueToPayment
                  )
                )}
              </button>
            </div>


          </div>
        )}


{step === 5 && !adminMode && (
          <PaymentStep
            amount={calculateTotal()}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            onPaymentAttemptStarted={handlePaymentAttemptStarted}
            onBack={() => setStep(4)}
            guestInfo={guestInfo}
            reservationData={{
              id: createdReservationId,
              checkIn: selectedCheckIn?.toISOString(),
              checkOut: selectedCheckOut?.toISOString(),
              selectedRoomType,
              selectedRoomIds: selectedRoomIds || [],
              selectedRoomCount: selectedRoomCount || {},
              selectedOccupancy: selectedOccupancy || {},
              selectedRoomOccupancies: selectedRoomOccupancies || {},
              displayPrices: displayPrices || {},
              selectedOptions: selectedOptions || [],
              appliedCoupon: appliedCoupon || null,
              total: calculateTotal(),
              optionsTotal: calculateOptionsTotal(),
              language,
              customAnswers: customAnswers || {},
              guestsDetails: guestsDetails || [],
              sessionId,
              temporaryBookingIds: temporaryBookingIds || [],
              ...(splitSegmentSelections.length > 0 ? {
                isSplitStay: true,
                splitSegments: splitSegmentSelections.map(seg => ({
                  checkIn: seg.checkIn instanceof Date ? seg.checkIn.toISOString() : seg.checkIn,
                  checkOut: seg.checkOut instanceof Date ? seg.checkOut.toISOString() : seg.checkOut,
                  selectedRoomIds: seg.selectedRoomIds || [],
                  selectedRoomCount: seg.selectedRoomCount || {},
                  selectedOccupancy: seg.selectedOccupancy || {},
                  selectedRoomOccupancies: seg.selectedRoomOccupancies || {},
                  displayPrices: seg.displayPrices || {},
                  segmentRoomTotal: seg.segmentRoomTotal || 0,
                }))
              } : {})
            }}
            language={language}
            t={t}
          />
        )}



      </div>

        {/* ✅ NOUVEAU : Formulaire de notification pour dates non ouvertes */}
      {/* Encart « Espace Club » — placé juste au-dessus du bloc « date pas encore ouverte ».
          Volontairement HORS de la boîte blanche du calendrier : à l'intérieur, les marges
          cumulées (boîte p-8 + conteneur py-8 + bloc gris py-6) créaient 89 px de vide sur mobile.
          Ordre voulu : on propose d'abord l'accès immédiat (membre), et seulement ensuite
          le lot de consolation (« prévenez-moi à l'ouverture »).
          Ce module n'a pas de mode membre : l'encart s'adresse donc à tout le monde.
          Tout le contenu vient de Paramètres > Général > Bloc « Espace Club » de VALETIER. */}
      {!adminMode && step === 1 && clubBlock.enabled && clubBlock.url &&
       (language === 'fr' ? clubBlock.textFr : clubBlock.textEn) && (
        <div className="max-w-6xl mx-auto px-2 sm:px-8">
          {/* Fond BLANC, seule la bordure jaune identifie le bloc : un logo contenant
              du jaune se noyait sur fond jaune, meme tres clair. */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-white border border-yellow-300 rounded-lg p-4">
            {clubBlock.logoUrl && (
              <img
                src={clubBlock.logoUrl}
                alt=""
                /* Hauteur calee, largeur libre : un logo panoramique reste lisible
                   la ou un cadre carre l'aurait reduit a une bandelette. */
                className="h-14 sm:h-16 w-auto max-w-[200px] object-contain flex-shrink-0 mx-auto sm:mx-0"
              />
            )}
            {/* Phrase centree dans l'espace restant entre le logo et le bouton */}
            <p className={`flex-1 text-sm text-gray-800 text-center ${clubBlock.textBold ? 'font-semibold' : ''}`}>
              {language === 'fr' ? clubBlock.textFr : clubBlock.textEn}
            </p>
            <a
              href={clubBlock.url}
              onClick={(e) => {
                // Le clic quitte le site : une ecriture Firestore lancee sans
                // attendre serait annulee par la navigation. On l'enregistre donc
                // AVANT de partir, mais plafonnee a 800 ms pour ne jamais faire
                // patienter le visiteur si le reseau traine.
                e.preventDefault();
                const partir = () => { window.location.href = clubBlock.url; };
                Promise.race([
                  logClubBlockClick({ page: window.location.pathname, language, clientIP: userIP }),
                  new Promise(resolve => setTimeout(resolve, 800))
                ]).then(partir, partir);
              }}
              className="w-full sm:w-auto px-6 py-3 bg-black text-white text-sm font-semibold uppercase tracking-wider hover:bg-gray-800 transition-colors rounded-lg text-center flex-shrink-0"
            >
              {(language === 'fr' ? clubBlock.buttonFr : clubBlock.buttonEn)
                || (language === 'fr' ? 'Espace membres' : 'Members area')}
            </a>
          </div>
        </div>
      )}

      {!adminMode && step === 1 && dateNotificationEnabled && (
        <DateNotificationForm
          language={language}
          customTitle={language === 'fr' ? dateNotificationTitleFr : dateNotificationTitleEn}
          customText={language === 'fr' ? dateNotificationTextFr : dateNotificationTextEn}
          maxPublicBookableDate={horizonSettings.maxPublicBookableDate}
        />
      )}

      {/* Module WhatsApp Last Minute - En bas de page */}
{!adminMode && step === 1 && (bookingWhatsappContentFr || bookingWhatsappContentEn || bookingWhatsappQrCodeUrl) && (
        <div className="bg-gray-50 border-t border-gray-200 pt-6 pb-44 sm:pb-28">
          <div className="max-w-4xl mx-auto px-8">
            <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                {/* Texte */}
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: language === 'fr' ? bookingWhatsappContentFr : bookingWhatsappContentEn 
                  }}
                />
                
                {/* QR Code + Lien */}
                {(bookingWhatsappQrCodeUrl || bookingWhatsappLink) && (
                  <div className="text-center">
                    {bookingWhatsappQrCodeUrl && (
                      <img 
                        src={bookingWhatsappQrCodeUrl} 
                        alt={language === 'fr' ? 'QR Code WhatsApp' : 'WhatsApp QR Code'} 
                        className="w-48 h-48 mx-auto mb-4 border border-gray-300 rounded-lg"
                      />
                    )}
                    {bookingWhatsappLink && (
                      <a 
                        href={bookingWhatsappLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-black underline hover:no-underline text-sm"
                      >
                        {language === 'fr' ? 'Groupe WhatsApp Dispo Last Minute' : 'Last Minute Availability WhatsApp Group'}
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Spacer universel step 1 — évite que le bandeau fixe masque le dernier élément */}
      {step === 1 && !adminMode && !(bookingWhatsappContentFr || bookingWhatsappContentEn || bookingWhatsappQrCodeUrl) && (
        <div className="h-28" />
      )}

{/* Modal de chargement avec dots animés */}
{loadingModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg p-8 max-w-md mx-4 border-2 border-black">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-6 text-black">
          {language === 'fr' ? 'Finalisation de votre réservation' : 'Finalizing your reservation'}
        </h2>
        
        <div className="flex justify-center items-center space-x-2 mb-6">
          <div className="w-3 h-3 bg-black rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-3 h-3 bg-black rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-3 h-3 bg-black rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
        
        <p className="text-gray-600">
          {language === 'fr' 
            ? 'Merci de patienter quelques instants...' 
            : 'Please wait a moment...'}
        </p>
      </div>
    </div>
  </div>
)}

      {confirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg p-6 sm:p-8 w-full max-w-md border-2 border-black">
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold mb-4 text-black">{t.bookingConfirmed}</h2>
              <p className="text-lg mb-2 text-gray-700">
                {t.bookedRooms.replace('{count}', confirmationModal.roomCount).replace(/{s}/g, confirmationModal.roomCount > 1 ? 's' : '')}
              </p>
              <p className="text-xl font-bold mb-6 text-black">
                {t.number} {confirmationModal.reservationNumber}
              </p>

              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setConfirmationModal(null);
                    setSplitStayInfo(null);
                    setSplitSegmentSelections([]);
                    setStep(1);
                    setSelectedCheckIn(null);
                    setSelectedCheckOut(null);
                    setSelectedRoomType(null);
                    setSelectedOccupancy({});
                    setSelectedRoomCount({});
                    setSelectedRoomOccupancies({});
                    setSelectedOptions([]);
                    setTemporaryBookingIds([]);
                    setSelectedRoomIds([]);
                    setGuestInfo({ name: '', firstName: '', company: '', email: '', phone: '', address: '', city: '', postalCode: '', country: language === 'fr' ? 'France' : 'United States', message: '' });
                    setCustomAnswers({});
                    setCgvAccepted(false);
                    setCgvError(false);
                    loadData();
                  }}
                  className="w-full px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 font-medium"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

{/* Modal d'erreur retour 3D Secure (vérification non aboutie / annulée) */}
      {threeDSError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg p-6 sm:p-8 w-full max-w-md border-2 border-red-600">
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto">
                  <X className="w-10 h-10 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">
                {language === 'fr' ? 'Vérification non aboutie' : 'Verification not completed'}
              </h2>
              <p className="text-gray-700 mb-6">
                {language === 'fr'
                  ? "La vérification de votre banque n'a pas pu être complétée et votre réservation n'a pas été finalisée. Aucun montant n'a été débité. Vous pouvez réessayer."
                  : 'Your bank verification could not be completed and your reservation was not finalized. No amount was charged. You can try again.'}
              </p>
              <button
                onClick={() => { setThreeDSError(false); setStep(1); }}
                className="w-full px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 font-medium"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

{/* Modal d'échec paiement après 3 tentatives */}
      {showPaymentFailedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md mx-4 border-2 border-red-600">
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto">
                  <X className="w-10 h-10 text-white" />
                </div>
              </div>
              <h2 className="text-2xl font-bold mb-4 text-red-600">
                {language === 'fr' ? 'Paiement échoué' : 'Payment failed'}
              </h2>
              <p className="text-gray-700 mb-6">
                {language === 'fr' 
                  ? 'Votre carte a été refusée après 3 tentatives. La réservation a été annulée. Veuillez réessayer avec une autre carte.'
                  : 'Your card was declined after 3 attempts. The reservation has been cancelled. Please try again with another card.'}
              </p>
              <button
                onClick={() => {
                  setShowPaymentFailedModal(false);
                  setPaymentAttempts(0);
                  setCreatedReservationId(null);
                  setStep(1);
                  setSelectedCheckIn(null);
                  setSelectedCheckOut(null);
                  setSelectedRoomType(null);
                  setSelectedOccupancy({});
                  setSelectedRoomCount({});
                  setSelectedRoomOccupancies({});
                  setSelectedOptions([]);
                  setTemporaryBookingIds([]);
                  setSelectedRoomIds([]);
                  setGuestInfo({
                    name: '',
                    firstName: '',
                    company: '',
                    email: '',
                    phone: '',
                    address: '',
                    city: '',
                    postalCode: '',
                    country: language === 'fr' ? 'France' : 'United States',
                    message: ''
                  });
                  setCustomAnswers({});
                  setCgvAccepted(false);
                  setCgvError(false);
                  loadData();
                }}
                className="px-8 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                {language === 'fr' ? 'Recommencer' : 'Start over'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de succès code coupon */}
      <Modal
        isOpen={showCouponSuccessModal}
        onClose={() => setShowCouponSuccessModal(false)}
        onConfirm={() => setShowCouponSuccessModal(false)}
        title={language === 'fr' ? 'Code appliqué' : 'Code applied'}
        confirmText="OK"
        cancelText=""
      >
        <div className="text-center py-4">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 rounded-full bg-black flex items-center justify-center">
              <Check className="w-6 h-6 text-white" />
            </div>
          </div>
          <p className="text-lg font-medium mb-2">
            {appliedCoupon?.description || (language === 'fr' ? 'Réduction' : 'Discount')}
          </p>
          <p className="text-gray-600">
            {language === 'fr' ? 'Code ' : 'Code '}
            <span className="font-mono font-semibold">{appliedCoupon?.code}</span>
            {language === 'fr' ? ' appliqué avec succès' : ' applied successfully'}
          </p>
        </div>
      </Modal>

      {/* Modal CGR */}
      {cgvModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setCgvModalOpen(false)}
        >
          <style>{`
            .cgr-content { white-space: pre-wrap; }
            .cgr-content strong { font-weight: 700; }
            .cgr-content em { font-style: italic; }
            .cgr-content u { text-decoration: underline; }
            .cgr-content p { margin-bottom: 1em; }
            .cgr-content ul { list-style-type: disc; padding-left: 2em; margin-bottom: 1em; }
            .cgr-content li { margin-bottom: 0.5em; }
          `}</style>
<div 
  className="bg-white rounded-lg w-full max-w-3xl max-h-[85vh] overflow-hidden shadow-2xl flex flex-col"
  onClick={(e) => e.stopPropagation()}
>

            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">{t.cgvLink}</h2>
              <button
                onClick={() => setCgvModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div 
                className="text-gray-700 leading-relaxed cgr-content"
                dangerouslySetInnerHTML={{ __html: cgvTexts[language] }}
              />
            </div>
            <div className="px-6 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
              <button
                onClick={() => setCgvModalOpen(false)}
                className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                {language === 'fr' ? 'Fermer' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicBooking;